import { useEffect, useRef, useState, type ClipboardEvent, type ReactNode } from 'react'
import { Badge } from '@/components/ui/Badge'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as Dialog from '@radix-ui/react-dialog'
import {
  AlertTriangle,
  Check,
  CheckSquare,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  Copy,
  FileText,
  Inbox,
  Link2Off,
  Loader2,
  MessageCircleMore,
  MoreHorizontal,
  Pause,
  Pencil,
  Plus,
  RefreshCw,
  Scissors,
  Trash2,
  X,
} from 'lucide-react'
import { AttachmentSection, DocumentPreview, EmailSourceSection, ManualSourceSection } from '../components/OcSource'
import { invalidateOcWorkflowData, workflowDetailQueryOptions } from '@/features/order-confirmation/workflowListQueries'
import { WORKFLOW_METRIC_COPY } from '@/features/order-confirmation/workflowMetricCopy'
import { ListRefreshIndicator } from '@/features/order-confirmation/components/ListRefreshIndicator'
import { useWorkflowOrderConfirmationSettings } from '@/features/order-confirmation/workflowOrderConfirmationSettingsQueries'
import { useSupplierSettings } from '@/features/order-confirmation/supplierSettingsQueries'
import {
  computeOcHeadSummaryDeviations,
  countConfiguredHeadDifferences,
  headFieldShouldHighlight,
  headValuesDiffer,
} from '@/features/order-confirmation/settings/headDifferenceChecks'
import {
  collectThresholdExceededLineDeviations,
  dateDeviation,
  daysEarly,
  daysLate,
  deviationClass,
  poRowQuantityDeviation,
  priceDeviation,
  totalConfirmedQuantityForPoRow,
  type DeviationLevel,
  type LineDeviationDetail,
} from '@/features/order-confirmation/settings/lineDeviationChecks'
import type { OcHeadComparisonFieldId } from '@/features/order-confirmation/settings/ocHeadComparisonFields'
import type { OrderConfirmationSettings } from '@/types/orderConfirmationSettings'
import { Tooltip } from '@/components/ui/Tooltip'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/DropdownMenu'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/Button'
import { Container } from '@/components/ui/Container'
import { toast } from '@/hooks/use-toast'
import {
  ApiRequestError,
  validateOrderConfirmation,
  cancelOrderConfirmation,
  markManualHandlingOrderConfirmation,
  createManualOrderConfirmation,
  createOrderConfirmationRow,
  deleteOrderConfirmationRow,
  duplicateOrderConfirmationRow,
  splitOrderConfirmationRowQuantities,
  getPurchaseOrderValidation,
  syncPurchaseOrderLatestVersion,
  pairOrderConfirmationRow,
  unpairOrderConfirmationRow,
  updateOrderConfirmationConfirmedQuantity,
  updateOrderConfirmationHead,
  type UpdateConfirmedQuantityPayload,
  type UpdateOrderConfirmationHeadPayload,
} from '@/services/api'
import type {
  LatestPurchaseOrderVersionSnapshot,
  OcChange,
  OrderConfirmationHistoryEntry,
  OrderConfirmationPurchaseOrderPair,
} from '@/types/purchaseOrderValidation'
import {
  confirmedQuantityArrivalDate,
  type OrderConfirmationHead,
  type OrderConfirmation,
  type OrderConfirmationRow,
  type UserReference,
} from '@/types/orderConfirmation'
import type { PurchaseOrder, PurchaseOrderHead, PurchaseOrderRow } from '@/types/purchaseOrder'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toDateInputValue(raw: string | undefined): string {
  if (!raw?.trim()) return ''
  const t = raw.trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t
  const d = new Date(t)
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10)
}

function parsePositiveQuantityInput(raw: string): number | null {
  const q = Number.parseFloat(raw.replace(',', '.').trim())
  if (!Number.isFinite(q) || q <= 0) return null
  return q
}

function rejectZeroQuantityPaste(e: ClipboardEvent<HTMLInputElement>) {
  const text = e.clipboardData.getData('text').trim()
  if (!text) return
  const q = Number.parseFloat(text.replace(',', '.'))
  if (Number.isFinite(q) && q <= 0) {
    e.preventDefault()
    toast({ variant: 'destructive', title: 'Quantity must be greater than zero' })
  }
}

function formatUnitPriceLabel(price: number | null | undefined, currency: string, unit: string): string | null {
  if (price == null || !Number.isFinite(price)) return null
  const formatted = `${price.toLocaleString('sv-SE')} ${currency}`
  const trimmedUnit = unit?.trim()
  return trimmedUnit ? `${formatted}/${trimmedUnit}` : formatted
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '-'
  try {
    const d = new Date(iso)
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  } catch { return iso }
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '-'
  try {
    const d = new Date(iso)
    const y = d.getFullYear()
    const mo = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    const h = String(d.getHours()).padStart(2, '0')
    const min = String(d.getMinutes()).padStart(2, '0')
    return `${y}-${mo}-${day} ${h}:${min}`
  } catch { return iso }
}

function poRowConfirmedDeliveryDate(row: PurchaseOrderRow): string | undefined {
  if (!row.is_confirmed) return undefined
  const date = row.confirmed_delivery_date?.trim()
  return date ? date : undefined
}


function ocSourceKind(oc: OrderConfirmation | undefined): 'manual' | 'email' | 'unknown' {
  if (!oc?.source) return 'unknown'
  if (oc.source.created_by) return 'manual'
  if (oc.source.email || oc.source.attachment) return 'email'
  return 'unknown'
}

const MANUAL_OC_CREATION_ENABLED = false

function manualOcBlockedReason(po: PurchaseOrder, pairs: OrderConfirmationPurchaseOrderPair[]): string | null {
  if (po.external_status === 'canceled') return 'Cannot create an OC for a canceled purchase order.'
  const blocking = new Set(['open', 'initialized'])
  if (pairs.some((p) => blocking.has((p.order_confirmation.internal_status ?? '').toLowerCase())))
    return 'Close existing open OCs before creating a new one.'
  return null
}

/** Mirrors workflow list tabs: /todo, /pending, /done. */
type PoWorkflowTabStatus = 'todo' | 'pending' | 'done'

const BLOCKING_OC_STATUSES = new Set(['open', 'queued', 'initialized'])

function hasBlockingOrderConfirmations(
  pairs: OrderConfirmationPurchaseOrderPair[],
  history: OrderConfirmationHistoryEntry[],
): boolean {
  if (history.some((entry) => BLOCKING_OC_STATUSES.has(entry.internal_status))) {
    return true
  }
  return pairs.some((pair) =>
    BLOCKING_OC_STATUSES.has((pair.order_confirmation.internal_status ?? '').toLowerCase()),
  )
}

function derivePoWorkflowTabStatus(
  pairs: OrderConfirmationPurchaseOrderPair[],
  history: OrderConfirmationHistoryEntry[],
  latestHead?: PurchaseOrderHead,
): PoWorkflowTabStatus {
  if (hasBlockingOrderConfirmations(pairs, history)) {
    return 'todo'
  }
  const head = latestHead ?? pairs[pairs.length - 1]?.purchase_order_head
  if (head?.is_confirmed) return 'done'
  return 'pending'
}

function canSyncLatestFromErp(
  pairs: OrderConfirmationPurchaseOrderPair[],
  history: OrderConfirmationHistoryEntry[],
  tabStatus: PoWorkflowTabStatus,
): boolean {
  if (tabStatus !== 'pending' && tabStatus !== 'done') return false
  return !hasBlockingOrderConfirmations(pairs, history)
}

function erpPurchaseOrderSnapshot(
  latestSnapshot: LatestPurchaseOrderVersionSnapshot | null | undefined,
  fallbackPair: OrderConfirmationPurchaseOrderPair | undefined,
) {
  return {
    head: latestSnapshot?.purchase_order_head ?? fallbackPair?.purchase_order_head,
    rows: latestSnapshot?.purchase_order_rows ?? fallbackPair?.purchase_order_rows ?? [],
  }
}

const PO_WORKFLOW_TAB_STATUS = {
  todo: {
    label: WORKFLOW_METRIC_COPY.todo.label,
    Icon: Inbox,
    className: 'border-blue-300 bg-blue-50 text-blue-800',
  },
  pending: {
    label: WORKFLOW_METRIC_COPY.pending.label,
    Icon: Clock,
    className: 'border-canvas-border/60 bg-surface text-gray-600',
  },
  done: {
    label: 'Done',
    Icon: CheckSquare,
    className: 'border-emerald-300 bg-emerald-50 text-emerald-800',
  },
} as const satisfies Record<PoWorkflowTabStatus, { label: string; Icon: typeof Inbox; className: string }>

function PoWorkflowTabStatusBadge({ status }: { status: PoWorkflowTabStatus }) {
  const { label, Icon, className } = PO_WORKFLOW_TAB_STATUS[status]
  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 rounded-lg border px-3.5 py-2 text-sm font-medium shadow-panel',
        className,
      )}
    >
      <Icon className="h-4 w-4 shrink-0 opacity-75" strokeWidth={2} aria-hidden />
      {label}
    </span>
  )
}

function PoWorkflowStatusBar({
  status,
  canSyncFromErp,
  onSync,
  syncing,
}: {
  status: PoWorkflowTabStatus
  canSyncFromErp: boolean
  onSync: () => void
  syncing: boolean
}) {
  return (
    <div className="ml-auto flex shrink-0 items-center gap-2">
      {canSyncFromErp && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" aria-label="Purchase order actions">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onSync} disabled={syncing}>
              <RefreshCw className={cn('mr-2 h-3.5 w-3.5 shrink-0', syncing && 'animate-spin')} />
              {syncing ? 'Syncing…' : 'Sync latest version from ERP'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <PoWorkflowTabStatusBadge status={status} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Three-panel navigation
// ---------------------------------------------------------------------------

type DetailViewMode = 'detail' | 'history' | 'queue'

function StackedDocIcon({ count, isActive, badge }: { count: number; isActive: boolean; badge?: ReactNode }) {
  return (
    <div className="relative h-11 w-9">
      {count >= 3 && (
        <div className={cn('absolute inset-0 translate-x-1 translate-y-1 rounded-sm border bg-white', isActive ? 'border-gray-700' : 'border-gray-300')} />
      )}
      {count >= 2 && (
        <div className={cn('absolute inset-0 translate-x-0.5 translate-y-0.5 rounded-sm border bg-white', isActive ? 'border-gray-700' : 'border-gray-300')} />
      )}
      <div className={cn('absolute inset-0 flex items-center justify-center rounded-sm border bg-white transition-colors', isActive ? 'border-gray-900 shadow-sm' : 'border-gray-300')}>
        <FileText className={cn('h-5 w-5 transition-colors', isActive ? 'text-gray-700' : 'text-gray-400')} />
      </div>
      {badge && (
        <span className="absolute -bottom-1.5 -right-1.5 z-20 flex h-4 w-4 items-center justify-center rounded-full border border-gray-300 bg-white shadow-sm">
          {badge}
        </span>
      )}
    </div>
  )
}

function ThreePanelNav({
  historyEntries,
  queueEntries,
  hasCurrentOc,
  viewMode,
  onSelect,
}: {
  historyEntries: OrderConfirmationHistoryEntry[]
  queueEntries: OrderConfirmationHistoryEntry[]
  hasCurrentOc: boolean
  viewMode: DetailViewMode
  onSelect: (mode: DetailViewMode) => void
}) {
  return (
    <div className="flex items-center gap-0">
      {historyEntries.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => onSelect(viewMode === 'history' ? 'detail' : 'history')}
            className="flex flex-col items-center gap-1.5 rounded-md p-1.5 transition-colors hover:bg-white/40"
            aria-pressed={viewMode === 'history'}
          >
            <StackedDocIcon
              count={Math.min(historyEntries.length + (hasCurrentOc ? 1 : 0), 3)}
              isActive={viewMode === 'history'}
              badge={<Check className="h-2.5 w-2.5 text-gray-600" strokeWidth={3} />}
            />
            <span className={cn('text-[10px] font-medium leading-none', viewMode === 'history' ? 'text-on-canvas' : 'text-on-canvas-muted')}>
              History
            </span>
          </button>
          {/* Only joins History to Current; the Queue draws its own connector. */}
          {hasCurrentOc && <div className="mb-[1.35rem] h-px w-3 shrink-0 bg-on-canvas/25" />}
        </>
      )}

      {hasCurrentOc && (
        <button
          type="button"
          onClick={() => onSelect('detail')}
          className="flex flex-col items-center gap-1.5 rounded-md p-1.5 transition-colors hover:bg-white/40"
          aria-pressed={viewMode === 'detail'}
        >
          <div className={cn(
            'relative flex h-11 w-9 items-center justify-center rounded-sm border bg-white transition-colors',
            viewMode === 'detail' ? 'border-gray-900 shadow-sm' : 'border-gray-300',
          )}>
            <FileText className={cn('h-5 w-5 transition-colors', viewMode === 'detail' ? 'text-gray-700' : 'text-gray-400')} />
          </div>
          <span className={cn('text-[10px] font-medium leading-none', viewMode === 'detail' ? 'text-on-canvas' : 'text-on-canvas-muted')}>
            Current
          </span>
        </button>
      )}

      {queueEntries.length > 0 && (
        <>
          <div className="mb-[1.35rem] h-px w-3 shrink-0 bg-on-canvas/25" />
          <button
            type="button"
            onClick={() => onSelect(viewMode === 'queue' ? 'detail' : 'queue')}
            className="flex flex-col items-center gap-1.5 rounded-md p-1.5 transition-colors hover:bg-white/40"
            aria-pressed={viewMode === 'queue'}
          >
            <StackedDocIcon
              count={Math.min(queueEntries.length + 1, 3)}
              isActive={viewMode === 'queue'}
              badge={<Pause className="h-2.5 w-2.5 text-gray-600" />}
            />
            <span className={cn('text-[10px] font-medium leading-none', viewMode === 'queue' ? 'text-on-canvas' : 'text-on-canvas-muted')}>
              Queue
            </span>
          </button>
        </>
      )}
    </div>
  )
}

function LookupIdList({
  purchaseOrderId,
  orderConfirmationId,
  className,
}: {
  purchaseOrderId: string
  orderConfirmationId?: string | null
  className?: string
}) {
  return (
    <ul className={cn('select-all space-y-0.5', className)}>
      <li className="font-mono text-[10px] leading-none text-gray-400">PO {purchaseOrderId}</li>
      {orderConfirmationId ? (
        <li className="font-mono text-[10px] leading-none text-gray-400">OC {orderConfirmationId}</li>
      ) : null}
    </ul>
  )
}

function OcDebugInfo({
  purchaseOrderId,
  orderConfirmationId,
}: {
  purchaseOrderId: string
  orderConfirmationId?: string | null
}) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between py-1 text-left"
      >
        <p className={HEAD_LABEL}>
          Debug info
        </p>
        {expanded
          ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" />
          : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
      </button>
      {expanded && (
        <LookupIdList
          purchaseOrderId={purchaseOrderId}
          orderConfirmationId={orderConfirmationId}
          className="mt-1.5"
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Source list view (queue grid — 3 per row)
// ---------------------------------------------------------------------------

function OcSourceListView({
  entries,
  pairs,
  purchaseOrderId,
  title,
  emptyMessage,
  columns = 2,
}: {
  entries: OrderConfirmationHistoryEntry[]
  pairs: OrderConfirmationPurchaseOrderPair[]
  purchaseOrderId: string
  title: string
  emptyMessage: string
  columns?: 2 | 3
}) {
  if (entries.length === 0) {
    return <div className="flex items-center justify-center py-16 text-sm text-gray-400">{emptyMessage}</div>
  }
  return (
    <div className="space-y-3">
      <h2 className="text-xs font-bold uppercase tracking-widest text-gray-500">{title}</h2>
      <div className={cn('grid gap-3', columns === 3 ? 'grid-cols-3' : 'grid-cols-2')}>
        {entries.map((entry) => {
          const pair = pairs.find((p) => p.order_confirmation.id === entry.id)
          const oc = pair?.order_confirmation
          return (
            <Container key={entry.id} className="overflow-hidden">
              <div className="border-b border-gray-100 px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500">{entry.label}</span>
                  {entry.is_validated && entry.validated_at && (
                    <Badge tone="neutral">
                      <Check className="h-2.5 w-2.5" strokeWidth={3} />
                      Validated {formatDate(entry.validated_at)}
                    </Badge>
                  )}
                </div>
                {entry.received_at && (
                  <span className="mt-0.5 block text-[10px] text-gray-400">
                    Arrived {formatDateTime(entry.received_at)}
                  </span>
                )}
              </div>
              <div className="p-3">
                <OCSourcePanel
                  oc={oc}
                  changes={pair?.order_confirmation_changes ?? []}
                  purchaseOrderId={purchaseOrderId}
                  emailDefaultExpanded={false}
                />
              </div>
            </Container>
          )
        })}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// History view — PO state panel (left) + OC history sidebar (right)
// ---------------------------------------------------------------------------

function PoSummaryPanel({
  po,
  head,
  rows,
  currency,
}: {
  po: PurchaseOrder
  head: PurchaseOrderHead | undefined
  rows: PurchaseOrderRow[]
  currency: string
}) {
  return (
    <Container className="flex min-w-0 flex-col overflow-hidden">
      <POHeaderPanel po={po} head={head} showStatus />
      {/* Line items on white down to the panel's bottom edge, as on the validation view. */}
      <div className="flex-1 border-t border-gray-200 bg-white">
        <div className="border-b border-gray-100 px-4 py-3">
          <span className="text-xs font-medium text-gray-500">Line items</span>
        </div>
        <PoRowsPanel rows={rows} currency={currency} />
      </div>
    </Container>
  )
}

function OcHistorySidebar({
  entries,
  pairs,
  purchaseOrderId,
  index,
  onPrev,
  onNext,
}: {
  entries: OrderConfirmationHistoryEntry[]
  pairs: OrderConfirmationPurchaseOrderPair[]
  purchaseOrderId: string
  index: number
  onPrev: () => void
  onNext: () => void
}) {
  const entry = entries[index]
  const pair = pairs.find((p) => p.order_confirmation.id === entry?.id)
  const oc = pair?.order_confirmation

  return (
    <Container className="flex min-w-0 flex-col overflow-hidden">
      <div className="bg-gray-50 px-4 py-4">
        <div className="mb-3 flex min-h-9 items-center justify-between border-b border-gray-300 pb-2.5">
          <p className="text-[11px] font-bold uppercase tracking-widest text-gray-500">Order confirmation</p>
        </div>

        {entries.length === 0 ? (
          <div className="flex items-center justify-center py-10 text-sm text-gray-400">No OC history</div>
        ) : (
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={onPrev}
              disabled={index <= 0}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-30"
              aria-label="Previous OC"
            >
              <ChevronLeft className="h-4 w-4 text-gray-500" />
            </button>

            <div className="flex min-w-0 flex-col items-center gap-1">
              <span className="text-sm font-semibold text-gray-900">{entry?.label}</span>
              {entry?.received_at && (
                <span className="text-xs text-gray-500">Arrived {formatDateTime(entry.received_at)}</span>
              )}
              <div className="flex flex-wrap items-center justify-center gap-1.5">
                {entry?.is_validated && entry.validated_at && (
                  <Badge tone="success">
                    <Check className="h-2.5 w-2.5" strokeWidth={3} />
                    Validated {formatDate(entry.validated_at)}
                  </Badge>
                )}
                {entry?.internal_status === 'canceled' && (
                  <Badge tone="danger">Canceled</Badge>
                )}
                {entry?.internal_status === 'manualHandling' && (
                  <Badge tone="neutral">Manual handling</Badge>
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={onNext}
              disabled={index >= entries.length - 1}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-30"
              aria-label="Next OC"
            >
              <ChevronRight className="h-4 w-4 text-gray-500" />
            </button>
          </div>
        )}
      </div>

      {entries.length > 0 && (
        <>
          <div className="border-t border-gray-200 p-4">
            <OCSourcePanel
              oc={oc}
              changes={pair?.order_confirmation_changes ?? []}
              purchaseOrderId={purchaseOrderId}
              emailDefaultExpanded={false}
            />
          </div>

          <div className="border-t border-gray-100 px-4 py-2 text-center">
            <span className="text-[10px] text-gray-400">{index + 1} / {entries.length}</span>
          </div>
        </>
      )}
    </Container>
  )
}

// ---------------------------------------------------------------------------
// Create OC dialog card
// ---------------------------------------------------------------------------

function CreateOcCard({ blocked, isCreating, onConfirm }: {
  blocked: string | null; isCreating: boolean; onConfirm: () => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Tooltip content={blocked ?? 'Add a manual order confirmation'}>
        <span className="inline-flex">
          <Dialog.Trigger asChild>
            <button type="button" disabled={blocked !== null || isCreating}
              className="flex flex-col items-center gap-1.5 rounded-md p-1.5 transition-colors hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-40"
              aria-label="Create order confirmation">
              <div className="relative flex h-11 w-9 items-center justify-center rounded-sm border border-dashed border-gray-300 bg-transparent">
                <FileText className="h-5 w-5 text-gray-200" />
                <span className="absolute -bottom-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full border border-gray-300 bg-white">
                  <Plus className="h-2.5 w-2.5 text-gray-500" strokeWidth={2.5} />
                </span>
              </div>
              <span className="text-[10px] font-medium leading-none text-gray-300">New</span>
            </button>
          </Dialog.Trigger>
        </span>
      </Tooltip>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/25 backdrop-blur-[1px]" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-panel bg-white p-6 shadow-lg">
          <Dialog.Title className="text-base font-semibold text-gray-900">Create order confirmation</Dialog.Title>
          <Dialog.Description className="mt-2 text-sm text-gray-500">
            Manually create a new Order Confirmation? This should only be done when a completion or correction is needed.
          </Dialog.Description>
          <div className="mt-5 flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="button" size="sm" disabled={isCreating} onClick={() => { onConfirm(); setOpen(false) }}>
              {isCreating ? 'Creating…' : 'Create'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

// ---------------------------------------------------------------------------
// Inline editable field
// ---------------------------------------------------------------------------

function InlineEditActions({ saving, onCancel, onConfirm }: {
  saving: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  return (
    <div className="flex shrink-0 items-center gap-0.5" aria-live="polite">
      <button
        type="button"
        onClick={onCancel}
        disabled={saving}
        aria-label="Cancel edit"
        className="flex h-6 w-6 items-center justify-center rounded border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <X className="h-3 w-3" />
      </button>
      <button
        type="button"
        onClick={onConfirm}
        disabled={saving}
        aria-label={saving ? 'Saving' : 'Save'}
        className="flex h-6 w-6 items-center justify-center rounded border border-gray-200 bg-white text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
      </button>
    </div>
  )
}

function EditableField({ label, value, poValue, readOnly, highlightDeviation = false, onSave, type = 'text', fieldId }: {
  label: string; value: string; poValue?: string; readOnly: boolean
  highlightDeviation?: boolean
  fieldId?: OcHeadComparisonFieldId
  onSave: (val: string) => Promise<void>; type?: 'text' | 'date'
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const [saving, setSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { if (editing) inputRef.current?.focus() }, [editing])
  useEffect(() => { setDraft(type === 'date' ? toDateInputValue(value) : value) }, [value, type])

  const isDeviation =
    highlightDeviation && headValuesDiffer(value, poValue, fieldId)

  async function handleSave() {
    setSaving(true)
    try { await onSave(draft); setEditing(false) }
    catch (err) { toast({ variant: 'destructive', title: 'Could not save', description: err instanceof Error ? err.message : 'Something went wrong.' }) }
    finally { setSaving(false) }
  }

  return (
    <div>
      <p className={HEAD_LABEL}>{label}</p>
      {editing ? (
        <div className="mt-0.5 flex items-center gap-1" onClick={(e) => e.stopPropagation()} aria-busy={saving}>
          <input
            ref={inputRef}
            type={type}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={saving}
            onKeyDown={(e) => {
              if (saving) return
              if (e.key === 'Enter') void handleSave()
              if (e.key === 'Escape') { setEditing(false); setDraft(value) }
            }}
            className={cn(
              'h-6 min-w-0 flex-1 rounded border border-input bg-white px-1.5 text-sm text-gray-900 shadow-sm outline-none focus-visible:border-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500/15 [appearance:textfield]',
              saving && 'cursor-wait bg-gray-50 text-gray-500',
            )}
          />
          <InlineEditActions
            saving={saving}
            onCancel={() => { setEditing(false); setDraft(value) }}
            onConfirm={() => void handleSave()}
          />
        </div>
      ) : (
        <button type="button" disabled={readOnly || saving}
          onClick={() => { setEditing(true); setDraft(type === 'date' ? toDateInputValue(value) : value) }}
          className={cn('group/ef mt-0.5 flex w-full items-center gap-1.5 rounded p-0 text-left', !readOnly && 'hover:bg-white/70')}>
          <span className={cn('flex-1 text-sm', isDeviation ? 'font-medium text-amber-700' : 'text-gray-800', !value && 'italic text-gray-300')}>
            {value || '-'}
          </span>
          {!readOnly && <Pencil className="h-3 w-3 shrink-0 text-gray-300 opacity-0 group-hover/ef:opacity-100" />}
        </button>
      )}
      {isDeviation && poValue && !editing && (
        <p className="mt-0.5 text-[10px] text-gray-400">In Purchase Order: {poValue}</p>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Deviation summary
// ---------------------------------------------------------------------------

interface PageStats {
  totalRows: number
  confirmedRows: number
  thresholdExceededLineDeviations: LineDeviationDetail[]
  quantityErrors: number
  uncoveredPoLines: number
  unmatchedOcRows: number
  unmatchedRows: number
  headDiffs: number
}

function computePageStats(
  poRows: PurchaseOrderRow[],
  unmatchedOcRows: OrderConfirmationRow[],
  ocHead: OrderConfirmationHead | undefined,
  poHead: PurchaseOrderHead | undefined,
  settings: OrderConfirmationSettings | undefined,
  highlightDeviations: boolean,
): PageStats {
  const confirmedRows = poRows.filter((r) => (r.matched_order_confirmation_rows ?? []).length > 0)
    .length
  const thresholdExceededLineDeviations = collectThresholdExceededLineDeviations(
    poRows,
    settings,
    highlightDeviations,
  )
  const quantityErrors = poRows.filter((r) => poRowQuantityDeviation(r) === 'error').length
  const headDiffs = countConfiguredHeadDifferences(ocHead, poHead, settings)
  const uncoveredPoLines = poRows.filter((r) => (r.matched_order_confirmation_rows ?? []).length === 0)
    .length
  const unmatchedOcRowCount = unmatchedOcRows.length

  return {
    totalRows: poRows.length,
    confirmedRows,
    thresholdExceededLineDeviations,
    quantityErrors,
    uncoveredPoLines,
    unmatchedOcRows: unmatchedOcRowCount,
    unmatchedRows: unmatchedOcRowCount + uncoveredPoLines,
    headDiffs,
  }
}

function ValidationStatBox({
  label,
  value,
  valueClassName,
  footer,
}: {
  label: ReactNode
  value: ReactNode
  valueClassName?: string
  footer?: ReactNode
}) {
  return (
    <div className="flex min-h-[2.5rem] min-w-[8.5rem] flex-col justify-center rounded-chip border border-canvas-border/60 bg-surface px-3.5 shadow-panel py-1.5 text-gray-700">
      <div className="flex w-full items-baseline justify-between gap-3">
        <p className="text-xs font-medium text-current/80">{label}</p>
        <p className={cn('text-sm font-medium leading-none tabular-nums', valueClassName ?? 'text-current')}>
          {value}
        </p>
      </div>
      {footer ? <div className="mt-0.5 text-center">{footer}</div> : null}
    </div>
  )
}

function ValidationSummaryPanels({
  stats,
}: {
  stats: PageStats
}) {
  const rowDeviations = stats.thresholdExceededLineDeviations.length

  return (
    <div className="flex flex-wrap items-center justify-center gap-2">
      <ValidationStatBox
        label="Matched rows"
        value={`${stats.confirmedRows}/${stats.totalRows}`}
        valueClassName="text-gray-900"
      />
      {rowDeviations > 0 && (
        <ValidationStatBox
          label="Row deviations"
          value={rowDeviations}
          valueClassName="text-amber-700"
        />
      )}
      {stats.quantityErrors > 0 && (
        <ValidationStatBox
          label="Quantity errors"
          value={stats.quantityErrors}
          valueClassName="text-red-600"
        />
      )}
      {stats.unmatchedOcRows > 0 && (
        <ValidationStatBox
          label="Unmatched rows"
          value={stats.unmatchedOcRows}
          valueClassName="text-amber-700"
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// PO header panel (left, read-only, expandable)
// ---------------------------------------------------------------------------

function FieldRow({ label, value }: { label: string; value: string | number | undefined | null }) {
  if (!value && value !== 0) return null
  return (
    <div>
      <p className={HEAD_LABEL}>{label}</p>
      <p className="mt-0.5 text-sm text-gray-900">{String(value)}</p>
    </div>
  )
}

type DeliveryAddressFields = {
  delivery_address_company_name?: string
  delivery_address_street?: string
  delivery_address_house_number?: string
  delivery_address_postal_code?: string
  delivery_address_city?: string
  delivery_address_country?: string
  delivery_address_additional_information?: string
}

function formatDeliveryAddressLines(head: DeliveryAddressFields): string[] {
  const streetLine = [head.delivery_address_street, head.delivery_address_house_number].filter(Boolean).join(' ')
  const cityLine = [head.delivery_address_postal_code, head.delivery_address_city].filter(Boolean).join(' ')
  return [
    head.delivery_address_company_name,
    streetLine,
    cityLine,
    head.delivery_address_country,
    head.delivery_address_additional_information,
  ].filter((line): line is string => Boolean(line?.trim()))
}

function formatQuotedComment(text: string): string {
  const trimmed = text.trim()
  if (!trimmed) return ''
  return `\u201C${trimmed}\u201D`
}

function CommentsPopover({ internal, external }: { internal?: string | null; external?: string | null }) {
  const hasInternal = Boolean(internal?.trim())
  const hasExternal = Boolean(external?.trim())
  if (!hasInternal && !hasExternal) return null
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center justify-center rounded-full p-0.5 hover:bg-gray-100/80"
          aria-label="View comments"
        >
          <MessageCircleMore className="h-5 w-5 fill-white stroke-gray-900" strokeWidth={2} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" side="bottom">
        <div className="space-y-3">
          {hasInternal && (
            <div>
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">Internal comment</p>
              <p className="select-text whitespace-pre-wrap text-sm text-gray-700">{formatQuotedComment(internal!)}</p>
            </div>
          )}
          {hasInternal && hasExternal && <div className="border-t border-gray-100" />}
          {hasExternal && (
            <div>
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">External comment</p>
              <p className="select-text whitespace-pre-wrap text-sm text-gray-700">{formatQuotedComment(external!)}</p>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

// Labels and values in the head panels, the same as the sales order page: a small
// uppercase label above each value.
const HEAD_LABEL = 'text-[10px] font-semibold uppercase tracking-wide text-gray-500'

function HeadSummaryField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <p className={HEAD_LABEL}>{label}</p>
      <div className="mt-0.5">{children}</div>
    </div>
  )
}

function headValueClass(value: string | undefined, deviated?: boolean) {
  return cn('text-sm', deviated ? 'font-medium text-amber-700' : value ? 'text-gray-900' : 'italic text-gray-300')
}

/**
 * The head of a purchase order or order confirmation, laid out like the sales order
 * head: name and number on the first row, delivery address beside the contact and
 * terms below. Deviations from the purchase order show in amber.
 */
function OrderDocumentHeadSummary({
  titleLabel,
  title,
  subtitleLabel,
  subtitle,
  sideNote,
  deliveryTerms,
  deliveryTermsDeviates,
  paymentTerms,
  paymentTermsDeviates,
  addressHead,
  addressDeviates,
}: {
  titleLabel: string
  title: string
  subtitleLabel: string
  subtitle: string
  sideNote?: { label: string; value: string; deviated?: boolean }
  deliveryTerms?: string
  deliveryTermsDeviates?: boolean
  paymentTerms?: string
  paymentTermsDeviates?: boolean
  addressHead?: DeliveryAddressFields
  addressDeviates?: boolean
}) {
  const addressLines = addressHead ? formatDeliveryAddressLines(addressHead) : []
  return (
    <div className="grid min-h-[10rem] grid-cols-2 gap-x-6 gap-y-4">
      <HeadSummaryField label={titleLabel}>
        <p className="text-lg font-semibold leading-tight text-gray-900">{title}</p>
      </HeadSummaryField>
      <HeadSummaryField label={subtitleLabel}>
        <p className="text-sm text-gray-900">{subtitle}</p>
      </HeadSummaryField>
      <HeadSummaryField label="Delivery address">
        {addressLines.length > 0 ? (
          <address className={cn('block text-sm not-italic leading-snug', addressDeviates ? 'font-medium text-amber-700' : 'text-gray-900')}>
            {addressLines.map((line, i) => (
              <span key={i} className="block">{line}</span>
            ))}
          </address>
        ) : (
          <p className={headValueClass(undefined)}>—</p>
        )}
      </HeadSummaryField>
      <div className="space-y-3">
        {sideNote !== undefined && (
          <HeadSummaryField label={sideNote.label}>
            <p className={headValueClass(sideNote.value, sideNote.deviated)}>{sideNote.value || '—'}</p>
          </HeadSummaryField>
        )}
        {deliveryTerms !== undefined && (
          <HeadSummaryField label="Delivery terms">
            <p className={headValueClass(deliveryTerms, deliveryTermsDeviates)}>{deliveryTerms || '—'}</p>
          </HeadSummaryField>
        )}
        {paymentTerms !== undefined && (
          <HeadSummaryField label="Payment terms">
            <p className={headValueClass(paymentTerms, paymentTermsDeviates)}>{paymentTerms || '—'}</p>
          </HeadSummaryField>
        )}
      </div>
    </div>
  )
}

function AddressBlock({ prefix, label, head }: {
  prefix: 'invoice_address' | 'delivery_address'; label: string; head: PurchaseOrderHead
}) {
  const f = (k: string) => ((head as unknown as Record<string, string>)[`${prefix}_${k}`]) ?? ''
  const line1 = [f('street'), f('house_number')].filter(Boolean).join(' ')
  const line2 = [f('postal_code'), f('city')].filter(Boolean).join(' ')
  const lines = [f('company_name'), line1, line2, f('country'), f('additional_information')].filter(Boolean)
  if (lines.length === 0) return null
  return (
    <div>
      <p className={HEAD_LABEL}>{label}</p>
      <div className="mt-0.5 text-sm text-gray-900">{lines.map((l, i) => <p key={i}>{l}</p>)}</div>
    </div>
  )
}

const headerActionSlotClass = 'flex h-6 w-6 shrink-0 items-center justify-center'

function DocumentPanelHeaderBar({
  label,
  expanded,
  onToggleExpanded,
  leadingAction,
  statusBadge,
  trailingLabel,
}: {
  label: string
  expanded: boolean
  onToggleExpanded: () => void
  leadingAction?: ReactNode
  statusBadge?: ReactNode
  /** Right-aligned, before the actions: e.g. the head differences. */
  trailingLabel?: ReactNode
}) {
  return (
    <div className="mb-3 flex min-h-9 items-center justify-between border-b border-gray-300 pb-2.5">
      <div className="flex items-center gap-2">
        <p className="text-[11px] font-bold uppercase tracking-widest text-gray-500">{label}</p>
        {statusBadge}
      </div>
      <div className="flex items-center gap-2">
        {trailingLabel}
        {leadingAction && <div className={headerActionSlotClass}>{leadingAction}</div>}
        <button type="button" onClick={onToggleExpanded} className={headerActionSlotClass} aria-expanded={expanded}>
          {expanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </button>
      </div>
    </div>
  )
}

function PoHeadStatusBadge({ head }: { head: PurchaseOrderHead | undefined }) {
  if (!head) return null
  if (head.is_canceled) {
    return (
      <Tooltip content="Canceled">
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-100">
          <X className="h-3 w-3 text-red-600" strokeWidth={3} />
        </span>
      </Tooltip>
    )
  }
  if (head.is_delivered || head.is_confirmed) {
    return (
      <Tooltip content="Confirmed">
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100">
          <Check className="h-3 w-3 text-emerald-700" strokeWidth={3} />
        </span>
      </Tooltip>
    )
  }
  return null
}

function POHeaderPanel({
  po,
  head,
  showStatus,
}: {
  po: PurchaseOrder
  head: PurchaseOrderHead | undefined
  showStatus?: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const hasComments = Boolean(head?.internal_comment?.trim() || head?.external_comment?.trim())

  return (
    <div className="px-4 py-4">
      <DocumentPanelHeaderBar
        label="Purchase order"
        expanded={expanded}
        onToggleExpanded={() => setExpanded((v) => !v)}
        leadingAction={hasComments ? <CommentsPopover internal={head!.internal_comment} external={head!.external_comment} /> : undefined}
        statusBadge={showStatus ? <PoHeadStatusBadge head={head} /> : undefined}
      />
      <button type="button" onClick={() => setExpanded((v) => !v)} className="w-full text-left">
        {head ? (
          <OrderDocumentHeadSummary
            titleLabel="Supplier"
            title={po.supplier_name}
            subtitleLabel="Purchase order number"
            subtitle={po.purchase_order_number}
            sideNote={po.purchaser ? { label: 'Purchaser', value: po.purchaser } : undefined}
            deliveryTerms={head.delivery_terms || undefined}
            paymentTerms={head.payment_terms || undefined}
            addressHead={head}
          />
        ) : (
          <div>
            <p className="text-lg font-semibold leading-tight text-gray-900">{po.supplier_name}</p>
            <p className="mt-0.5 font-mono text-sm text-gray-500">{po.purchase_order_number}</p>
          </div>
        )}
      </button>

      {expanded && head && (
        <div className="mt-4 space-y-4 border-t border-gray-200 pt-4">
          <div className="grid grid-cols-2 gap-4">
            <FieldRow label="ERP ID" value={head.erp_id} />
            <FieldRow label="Customer order no." value={head.customer_order_number} />
            <FieldRow label="Supplier order no." value={head.supplier_order_number} />
            <FieldRow label="Supplier ID" value={head.supplier_id} />
            <FieldRow label="Supplier number" value={head.supplier_number} />
            <FieldRow label="Supplier contact" value={head.supplier_contact_person} />
            <FieldRow label="Customer contact" value={head.customer_contact_person} />
            <FieldRow label="Currency" value={head.currency} />
            <FieldRow label="Creation date" value={head.creation_date} />
            <FieldRow label="Purchaser" value={po.purchaser} />
            <FieldRow label="Payment terms" value={head.payment_terms} />
            <FieldRow label="Delivery terms" value={head.delivery_terms} />
            <FieldRow label="Delivery method" value={head.delivery_method} />
            <FieldRow label="Warehouse" value={head.warehouse} />
            <FieldRow label="Type" value={head.type} />
            <FieldRow label="Goods label" value={head.goods_label} />
            {head.transport_lead_time_in_days > 0 && (
              <FieldRow label="Transport lead time" value={`${head.transport_lead_time_in_days} days`} />
            )}
          </div>
          <AddressBlock prefix="invoice_address" label="Invoice address" head={head} />
          <AddressBlock prefix="delivery_address" label="Delivery address" head={head} />
          {head.internal_comment?.trim() && (
            <div>
              <p className={HEAD_LABEL}>Internal comment</p>
              <p className="mt-0.5 text-sm text-gray-700">{formatQuotedComment(head.internal_comment)}</p>
            </div>
          )}
          {head.external_comment?.trim() && (
            <div>
              <p className={HEAD_LABEL}>External comment</p>
              <p className="mt-0.5 text-sm text-gray-700">{formatQuotedComment(head.external_comment)}</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// OC header panel (right, editable, expandable - mirrors PO head style)
// ---------------------------------------------------------------------------

function OCHeaderPanel({
  ocHead,
  poHead,
  readOnly,
  highlightDeviations,
  settings,
  onSave,
}: {
  ocHead: OrderConfirmationHead | undefined
  poHead: PurchaseOrderHead | undefined
  readOnly: boolean
  highlightDeviations: boolean
  settings: OrderConfirmationSettings | undefined
  onSave: (patch: UpdateOrderConfirmationHeadPayload) => Promise<void>
}) {
  const [expanded, setExpanded] = useState(false)
  if (!ocHead) return null

  const poVal = (k: keyof PurchaseOrderHead) => poHead ? String(poHead[k] ?? '') : undefined
  const field = (
    label: string,
    key: keyof UpdateOrderConfirmationHeadPayload & OcHeadComparisonFieldId,
    poKey?: keyof PurchaseOrderHead,
    type?: 'text' | 'date',
  ) => (
    <EditableField
      key={key}
      label={label}
      value={String(ocHead[key as keyof OrderConfirmationHead] ?? '')}
      poValue={poKey ? poVal(poKey) : undefined}
      readOnly={readOnly}
      fieldId={key}
      highlightDeviation={
        highlightDeviations && headFieldShouldHighlight(ocHead, poHead, settings, key)
      }
      type={type}
      onSave={(val) => onSave({ [key]: val })}
    />
  )

  const headDiffCount = highlightDeviations
    ? countConfiguredHeadDifferences(ocHead, poHead, settings)
    : 0
  const headDeviations = computeOcHeadSummaryDeviations(ocHead, poHead, settings, highlightDeviations)
  const supplierContact = ocHead.supplier_contact_person || ''

  return (
    <div className="px-4 py-4">
      <DocumentPanelHeaderBar
        label="Order confirmation"
        expanded={expanded}
        onToggleExpanded={() => setExpanded((v) => !v)}
        trailingLabel={headDiffCount > 0 ? (
          <Badge tone="warning" dot>{headDiffCount} head {headDiffCount === 1 ? 'diff' : 'diffs'}</Badge>
        ) : undefined}
      />
      <button type="button" onClick={() => setExpanded((v) => !v)} className="w-full text-left">
        <OrderDocumentHeadSummary
          titleLabel="Supplier"
          title={ocHead.supplier_name || '-'}
          subtitleLabel="Supplier order number"
          subtitle={ocHead.supplier_order_number || '-'}
          sideNote={{ label: 'Supplier contact', value: supplierContact, deviated: headDeviations.sideNote }}
          deliveryTerms={ocHead.delivery_terms}
          deliveryTermsDeviates={headDeviations.deliveryTerms}
          paymentTerms={ocHead.payment_terms}
          paymentTermsDeviates={headDeviations.paymentTerms}
          addressHead={ocHead}
          addressDeviates={headDeviations.address}
        />
      </button>

      {expanded && (
        <div className="mt-4 space-y-4 border-t border-gray-200 pt-4">
          <div className="grid grid-cols-2 gap-4">
            {field('Customer order no.', 'customer_order_number', 'customer_order_number')}
            {field('Supplier order no.', 'supplier_order_number', 'supplier_order_number')}
            {field('Supplier name', 'supplier_name', 'supplier_name')}
            {field('Supplier contact', 'supplier_contact_person', 'supplier_contact_person')}
            {field('Payment terms', 'payment_terms', 'payment_terms')}
            {field('Delivery terms', 'delivery_terms', 'delivery_terms')}
            {field('Delivery method', 'delivery_method', 'delivery_method')}
          </div>
          <div className="grid grid-cols-2 gap-4 border-t border-gray-100 pt-4">
            <div className="space-y-3">
              <p className={HEAD_LABEL}>Invoice address</p>
              {field('Company', 'invoice_address_company_name', 'invoice_address_company_name')}
              {field('Street', 'invoice_address_street', 'invoice_address_street')}
              {field('House no.', 'invoice_address_house_number', 'invoice_address_house_number')}
              {field('Postal code', 'invoice_address_postal_code', 'invoice_address_postal_code')}
              {field('City', 'invoice_address_city', 'invoice_address_city')}
              {field('Country', 'invoice_address_country', 'invoice_address_country')}
              {field('Additional info', 'invoice_address_additional_information', 'invoice_address_additional_information')}
            </div>
            <div className="space-y-3">
              <p className={HEAD_LABEL}>Delivery address</p>
              {field('Company', 'delivery_address_company_name', 'delivery_address_company_name')}
              {field('Street', 'delivery_address_street', 'delivery_address_street')}
              {field('House no.', 'delivery_address_house_number', 'delivery_address_house_number')}
              {field('Postal code', 'delivery_address_postal_code', 'delivery_address_postal_code')}
              {field('City', 'delivery_address_city', 'delivery_address_city')}
              {field('Country', 'delivery_address_country', 'delivery_address_country')}
              {field('Additional info', 'delivery_address_additional_information', 'delivery_address_additional_information')}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// OC source panel
// ---------------------------------------------------------------------------

// EmailSourceSection, ManualSourceSection, AttachmentSection, DocumentPreview
// are imported from ../components/OcSource

// ValidatedBySection names who validated this confirmation — a person, or the
// FrontFlow Automation user when it went through untouched.
function ValidatedBySection({ validatedBy }: { validatedBy: UserReference }) {
  const name = `${validatedBy.first_name} ${validatedBy.last_name}`.trim() || validatedBy.email_address

  return (
    <div>
      <p className={HEAD_LABEL}>
        Validated by
      </p>
      <p className="mt-1.5 text-xs text-gray-700">{name}</p>
    </div>
  )
}

function OCSourcePanel({ oc, changes, purchaseOrderId, emailDefaultExpanded = true }: {
  oc: OrderConfirmation | undefined
  changes: OcChange[]
  purchaseOrderId: string
  emailDefaultExpanded?: boolean
}) {
  const hasAuditLog = changes.length > 0

  return (
    <div className="space-y-4">
      {oc?.source.attachment && (
        <>
          <AttachmentSection attachment={oc.source.attachment} />
          <div className="border-t border-gray-100" />
        </>
      )}
      {oc ? (
        oc.source.email
          ? <EmailSourceSection
              label="Source"
              email={oc.source.email!}
              attachment={oc.source.attachment}
              resetKey={oc.id}
              defaultExpanded={emailDefaultExpanded}
            />
          : <ManualSourceSection label="Source" oc={oc} />
      ) : (
        <DocumentPreview />
      )}
      {oc?.validated_by && (
        <>
          <div className="border-t border-gray-100" />
          <ValidatedBySection validatedBy={oc.validated_by} />
        </>
      )}
      {hasAuditLog && (
        <>
          <div className="border-t border-gray-100" />
          <OcAuditLog changes={changes} />
        </>
      )}
      <div className="border-t border-gray-100" />
      <OcDebugInfo purchaseOrderId={purchaseOrderId} orderConfirmationId={oc?.id} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Line items table
// ---------------------------------------------------------------------------

interface DisplayConfirmedLine {
  quantity: number
  /** Display value; falls back to delivery date when arrival is unset. */
  arrival_date: string
  /** Raw arrival date used for discrepancy checks (no delivery fallback). */
  comparison_arrival_date: string
  delivery_date: string
  unit_price: number | null
  currency: string
  order_confirmation_row_id: string
  confirmed_quantity_id?: number | null
  cq_index: number
}

function ocLineKey(line: DisplayConfirmedLine): string {
  if (line.confirmed_quantity_id != null) return `cq-${line.confirmed_quantity_id}`
  return `${line.order_confirmation_row_id}-${line.cq_index}`
}

function confirmedLinesForOcRow(ocRow: OrderConfirmationRow, currency: string): DisplayConfirmedLine[] {
  const cqs = ocRow.confirmed_quantities ?? []
  return cqs.map((cq, i) => ({
    quantity: cq.quantity,
    arrival_date: confirmedQuantityArrivalDate(cq),
    comparison_arrival_date: cq.arrival_date?.trim() ?? '',
    delivery_date: cq.delivery_date ?? '',
    unit_price: ocRow.unit_price,
    currency,
    order_confirmation_row_id: ocRow.id,
    confirmed_quantity_id: cq.id ?? null,
    cq_index: i,
  }))
}

function flattenConfirmedLines(matchedOcRows: OrderConfirmationRow[] | null | undefined, currency: string): DisplayConfirmedLine[] {
  const out: DisplayConfirmedLine[] = []
  for (const ocRow of matchedOcRows ?? []) {
    out.push(...confirmedLinesForOcRow(ocRow, currency))
  }
  return out
}

function confirmedLinesHaveDeviation(
  row: PurchaseOrderRow,
  lines: DisplayConfirmedLine[],
  settings: OrderConfirmationSettings | undefined,
): boolean {
  if (poRowQuantityDeviation(row) !== 'none') return true
  return lines.some(
    (l) => dateDeviation(l.arrival_date, row, settings, true) !== 'none' || priceDeviation(l.unit_price, row, settings, true) !== 'none',
  )
}

type OcLineEditField = 'quantity' | 'dates'

function OcLineCompact({ line, unit, row, settings, editingField, isSaving, readOnly, highlightDeviations, quantityDeviation, onRequestEdit, onCancelEdit, onSaveField }: {
  line: DisplayConfirmedLine; unit: string; row: PurchaseOrderRow
  settings: OrderConfirmationSettings | undefined
  editingField: OcLineEditField | null; isSaving: boolean; readOnly: boolean; highlightDeviations: boolean
  quantityDeviation: DeviationLevel
  onRequestEdit: (f: OcLineEditField) => void; onCancelEdit: () => void
  onSaveField: (_f: OcLineEditField, patch: UpdateConfirmedQuantityPayload) => Promise<void>
}) {
  const [draftQty, setDraftQty] = useState(String(line.quantity))
  const [draftDeliveryDate, setDraftDeliveryDate] = useState(() => toDateInputValue(line.delivery_date))
  const [draftArrivalDate, setDraftArrivalDate] = useState(() => toDateInputValue(line.arrival_date))
  // Set on a save attempt with a date left empty: marks the empty fields until they are filled.
  const [showMissingDates, setShowMissingDates] = useState(false)

  useEffect(() => { if (editingField === 'quantity') setDraftQty(String(line.quantity)) }, [editingField, line.quantity])
  useEffect(() => {
    if (editingField === 'dates') {
      setDraftDeliveryDate(toDateInputValue(line.delivery_date))
      setDraftArrivalDate(toDateInputValue(line.arrival_date))
      setShowMissingDates(false)
    }
  }, [editingField, line.delivery_date, line.arrival_date])
  const deliveryMissing = showMissingDates && !draftDeliveryDate.trim()
  const arrivalMissing = showMissingDates && !draftArrivalDate.trim()

  const dateDev = dateDeviation(line.comparison_arrival_date, row, settings, highlightDeviations)
  const priceHighlight = priceDeviation(line.unit_price, row, settings, highlightDeviations)
  const lateDays = daysLate(line.comparison_arrival_date, row)
  const earlyDays = daysEarly(line.comparison_arrival_date, row)
  const pricePct = priceHighlight !== 'none' && row.unit_price > 0 && line.unit_price != null
    ? ((line.unit_price - row.unit_price) / row.unit_price) * 100
    : 0
  const unitPriceLabel = formatUnitPriceLabel(line.unit_price, line.currency, unit)
  const canEdit = !readOnly && line.confirmed_quantity_id != null && !isSaving

  return (
    <div className="space-y-1" aria-busy={isSaving}>
      {editingField === 'quantity' ? (
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <input type="number" step="any" min={0} value={draftQty} onChange={(e) => setDraftQty(e.target.value)} onPaste={rejectZeroQuantityPaste} disabled={isSaving}
            className={cn('h-6 w-20 rounded border border-input bg-white px-1.5 text-sm tabular-nums text-gray-900 shadow-sm outline-none focus-visible:border-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500/15 [appearance:textfield]', isSaving && 'cursor-wait bg-gray-50 text-gray-500')} />
          <span className={cn('text-sm text-gray-900', isSaving && 'text-gray-400')}>{unit}</span>
          <InlineEditActions saving={isSaving} onCancel={onCancelEdit}
            onConfirm={() => {
              const q = parsePositiveQuantityInput(draftQty)
              if (q == null) { toast({ variant: 'destructive', title: 'Quantity must be greater than zero' }); return }
              void onSaveField('quantity', { quantity: q })
            }} />
        </div>
      ) : (
        <button type="button" disabled={!canEdit} onClick={() => canEdit && onRequestEdit('quantity')}
          className={cn('group/edit flex items-center gap-1.5 rounded p-0 text-left text-sm text-gray-900', canEdit && 'hover:bg-white/60')}>
          <span className={deviationClass(quantityDeviation)}>{line.quantity} {unit}</span>
          {canEdit && <Pencil className="h-3 w-3 text-gray-300 opacity-0 group-hover/edit:opacity-100" />}
        </button>
      )}

      {editingField === 'dates' ? (
        <div className="space-y-1.5 rounded border border-gray-200 bg-white p-2 shadow-sm" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-2">
            <span className={cn('w-32 shrink-0 text-[10px] font-medium', deliveryMissing ? 'text-red-600' : 'text-gray-600')}>Extracted delivery</span>
            <input type="date" required aria-invalid={deliveryMissing} value={draftDeliveryDate} onChange={(e) => setDraftDeliveryDate(e.target.value)} disabled={isSaving}
              className={cn('h-6 w-auto max-w-[9rem] rounded border border-input bg-white px-1.5 text-[11px] shadow-sm outline-none focus-visible:border-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500/15', deliveryMissing && 'border-red-500', isSaving && 'cursor-wait bg-gray-50 text-gray-500')} />
          </div>
          <div className="flex items-center gap-2">
            <span className={cn('w-32 shrink-0 text-[10px] font-medium', arrivalMissing ? 'text-red-600' : 'text-gray-600')}>Arrival date *</span>
            <input type="date" required aria-invalid={arrivalMissing} value={draftArrivalDate} onChange={(e) => setDraftArrivalDate(e.target.value)} disabled={isSaving}
              className={cn('h-6 w-auto max-w-[9rem] rounded border border-input bg-white px-1.5 text-[11px] shadow-sm outline-none focus-visible:border-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500/15', arrivalMissing && 'border-red-500', isSaving && 'cursor-wait bg-gray-50 text-gray-500')} />
          </div>
          <div className="flex items-center justify-between gap-2">
            <p className={cn('text-[10px]', showMissingDates && (deliveryMissing || arrivalMissing) ? 'text-red-600' : 'text-gray-400')}>
              {showMissingDates && (deliveryMissing || arrivalMissing)
                ? 'Both dates are needed for the order to validate.'
                : '* Arrival date is the delivery date with lead times, weekends and holidays included.'}
            </p>
            <InlineEditActions saving={isSaving} onCancel={onCancelEdit}
              onConfirm={() => {
                // Both dates are required: the order can't be validated with either one missing.
                if (!draftDeliveryDate.trim() || !draftArrivalDate.trim()) { setShowMissingDates(true); return }
                void onSaveField('dates', { delivery_date: draftDeliveryDate.trim(), arrival_date: draftArrivalDate.trim() })
              }} />
          </div>
        </div>
      ) : (
        <button type="button" disabled={!canEdit} onClick={() => canEdit && onRequestEdit('dates')}
          className={cn('group/edit flex items-center gap-1.5 rounded p-0 text-left', canEdit && 'hover:bg-white/60')}>
          <span className={cn('text-xs', dateDev !== 'none' ? deviationClass(dateDev) : line.arrival_date ? 'text-gray-500' : 'italic text-gray-300')}>
            {line.arrival_date || '—'}
            {line.arrival_date && line.delivery_date && line.delivery_date !== line.arrival_date && (
              <span className="font-normal text-gray-400"> ({line.delivery_date})</span>
            )}
            {dateDev !== 'none' && lateDays > 0 && (
              <span className="font-normal text-gray-400"> · {lateDays}d late</span>
            )}
            {dateDev !== 'none' && earlyDays > 0 && (
              <span className="font-normal text-gray-400"> · {earlyDays}d early</span>
            )}
          </span>
          {canEdit && <Pencil className="h-3 w-3 text-gray-300 opacity-0 group-hover/edit:opacity-100" />}
        </button>
      )}

      {unitPriceLabel && (
        <p className={cn('text-xs', priceHighlight !== 'none' ? deviationClass(priceHighlight) : 'text-gray-400')}>
          {unitPriceLabel}
          {priceHighlight !== 'none' && ` (${pricePct >= 0 ? '+' : ''}${pricePct.toFixed(1)}%)`}
        </p>
      )}
    </div>
  )
}

function MatchedRow({ row, currency, workflowId, purchaseOrderId, ocId, isManualOc, readOnly, highlightDeviations, settings, matchingMode, onRowClick }: {
  row: PurchaseOrderRow; currency: string; workflowId: string; purchaseOrderId: string; ocId: string; isManualOc: boolean; readOnly: boolean; highlightDeviations: boolean
  settings: OrderConfirmationSettings | undefined
  matchingMode: boolean
  onRowClick?: () => void
}) {
  const queryClient = useQueryClient()
  const [fieldEdit, setFieldEdit] = useState<{ lineKey: string; field: OcLineEditField } | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [addQty, setAddQty] = useState('')
  const [addDate, setAddDate] = useState('')

  const updateMutation = useMutation({
    mutationFn: async (vars: { line: DisplayConfirmedLine; patch: UpdateConfirmedQuantityPayload }) => {
      if (vars.line.confirmed_quantity_id == null) throw new Error('Missing id')
      await updateOrderConfirmationConfirmedQuantity(String(vars.line.confirmed_quantity_id), vars.patch)
    },
    onSuccess: (_d, vars) => {
      invalidateOcWorkflowData(queryClient, workflowId, { purchaseOrderId })
      setFieldEdit(null)
      toast({ title: vars.patch.quantity !== undefined ? 'Quantity updated' : 'Dates updated' })
    },
    onError: (err: unknown) => toast({ variant: 'destructive', title: 'Could not save', description: err instanceof Error ? err.message : 'Something went wrong.' }),
  })

  const unpairMutation = useMutation({
    mutationFn: (ocRowId: string) => unpairOrderConfirmationRow(ocRowId),
    onSuccess: () => {
      invalidateOcWorkflowData(queryClient, workflowId, { purchaseOrderId })
      toast({ title: 'Row unmatched' })
    },
    onError: (err: unknown) => toast({ variant: 'destructive', title: 'Could not unmatch', description: err instanceof Error ? err.message : 'Something went wrong.' }),
  })

  const duplicateMutation = useMutation({
    mutationFn: (ocRowId: string) => duplicateOrderConfirmationRow(ocRowId),
    onSuccess: () => {
      invalidateOcWorkflowData(queryClient, workflowId, { purchaseOrderId })
      toast({ title: 'Row duplicated' })
    },
    onError: (err: unknown) => toast({ variant: 'destructive', title: 'Could not duplicate', description: err instanceof Error ? err.message : 'Something went wrong.' }),
  })

  const splitQuantitiesMutation = useMutation({
    mutationFn: (ocRowId: string) => splitOrderConfirmationRowQuantities(ocRowId),
    onSuccess: () => {
      invalidateOcWorkflowData(queryClient, workflowId, { purchaseOrderId })
      toast({ title: 'Quantities split into separate rows' })
    },
    onError: (err: unknown) => toast({ variant: 'destructive', title: 'Could not split quantities', description: err instanceof Error ? err.message : 'Something went wrong.' }),
  })

  const createRowMutation = useMutation({
    mutationFn: (vars: { quantity: number; delivery_date: string }) =>
      createOrderConfirmationRow(ocId, { purchase_order_row_id: row.id, ...vars }),
    onSuccess: () => {
      setShowAddForm(false)
      setAddQty('')
      setAddDate('')
      invalidateOcWorkflowData(queryClient, workflowId, { purchaseOrderId })
      toast({ title: 'Row added' })
    },
    onError: (err: unknown) => toast({ variant: 'destructive', title: 'Could not add row', description: err instanceof Error ? err.message : 'Something went wrong.' }),
  })

  const matchedOcRows = row.matched_order_confirmation_rows ?? []
  const confirmedLines = flattenConfirmedLines(matchedOcRows, currency)
  const isUnmatched = confirmedLines.length === 0
  // Quantity, date or price: the confirmation's cell is tinted amber; the PO side stays white.
  // A row the confirmation doesn't cover already says "Not found", so it gets no badge.
  const hasDeviation = highlightDeviations && !isUnmatched && confirmedLinesHaveDeviation(row, confirmedLines, settings)
  const poConfirmedDate = poRowConfirmedDeliveryDate(row)
  const quantityDeviation = poRowQuantityDeviation(row)
  const unitPriceLabel = formatUnitPriceLabel(row.unit_price, currency, row.unit)

  return (
    <div
      id={`oc-row-pos-${row.position}`}
      className="grid grid-cols-2 scroll-mt-24 border-t border-gray-100"
    >
      {/* PO side */}
      <div
        className={cn('px-4 py-3', matchingMode && onRowClick && 'cursor-pointer transition-colors hover:bg-blue-50/60')}
        onClick={matchingMode && onRowClick ? onRowClick : undefined}
      >
        <div className="flex items-baseline gap-2">
          <span className="text-[10px] font-semibold text-gray-400">#{row.position}</span>
          {row.article_number && <span className="font-mono text-[10px] text-gray-400">{row.article_number}</span>}
        </div>
        <p className="mt-0.5 text-sm font-medium leading-snug text-gray-900">
          {row.article_description || row.article_number || '—'}
        </p>
        {row.additional_information?.trim() && (
          <p className="mt-0.5 text-xs text-gray-500">{row.additional_information}</p>
        )}
        <div className="mt-1.5 space-y-0.5">
          <p className={cn('text-sm text-gray-700', deviationClass(quantityDeviation))}>
            {row.requested_quantity} {row.unit}
            {quantityDeviation !== 'none' && (
              <span className={cn('ml-1.5 text-xs font-normal', deviationClass(quantityDeviation))}>
                (confirmed: {totalConfirmedQuantityForPoRow(row)})
              </span>
            )}
          </p>
          <p className="text-xs text-gray-400">{formatDate(row.requested_delivery_date)}</p>
          {unitPriceLabel && (
            <p className="text-xs text-gray-400">{unitPriceLabel}</p>
          )}
        </div>
        {poConfirmedDate && (
          <p className="mt-2 text-[10px] text-gray-400">ERP confirmed: {formatDate(poConfirmedDate)}</p>
        )}
      </div>

      {/* OC side */}
      <div className={cn('border-l border-gray-200 px-4 py-3', hasDeviation && 'bg-amber-50')}>
        {isUnmatched ? (
          isManualOc && !readOnly ? (
            showAddForm ? (
              <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                <div className="space-y-1.5">
                  <div>
                    <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">Quantity</p>
                    <input
                      type="number"
                      step="any"
                      min={0}
                      placeholder={String(row.requested_quantity)}
                      value={addQty}
                      onChange={(e) => setAddQty(e.target.value)}
                      onPaste={rejectZeroQuantityPaste}
                      disabled={createRowMutation.isPending}
                      className="h-7 w-full rounded border border-input bg-white px-2 text-sm text-gray-900 shadow-sm outline-none focus-visible:border-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500/15 [appearance:textfield] disabled:opacity-50"
                    />
                  </div>
                  <div>
                    <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">Delivery date</p>
                    <input
                      type="date"
                      value={addDate}
                      onChange={(e) => setAddDate(e.target.value)}
                      disabled={createRowMutation.isPending}
                      className="h-7 w-full rounded border border-input bg-white px-2 text-[11px] shadow-sm outline-none focus-visible:border-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500/15 disabled:opacity-50"
                    />
                  </div>
                </div>
                <InlineEditActions
                  saving={createRowMutation.isPending}
                  onCancel={() => { setShowAddForm(false); setAddQty(''); setAddDate('') }}
                  onConfirm={() => {
                    const q = parsePositiveQuantityInput(addQty)
                    if (q == null) { toast({ variant: 'destructive', title: 'Quantity must be greater than zero' }); return }
                    if (!addDate.trim()) { toast({ variant: 'destructive', title: 'Delivery date required' }); return }
                    void createRowMutation.mutateAsync({ quantity: q, delivery_date: addDate.trim() })
                  }}
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setShowAddForm(true); setAddQty(''); setAddDate('') }}
                className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600"
              >
                <Plus className="h-3.5 w-3.5" />
                Add row
              </button>
            )
          ) : !isManualOc ? (
            <span className="text-xs italic text-gray-400">Not found in this Order Confirmation</span>
          ) : null
        ) : (
          <div className="space-y-4">
            {matchedOcRows.map((ocRow, rowIdx) => {
              const ocArticleNumber = ocRow.supplier_article_number || ocRow.customer_article_number || ''
              const lines = confirmedLinesForOcRow(ocRow, currency)
              const isUnpairing = unpairMutation.isPending && unpairMutation.variables === ocRow.id
              return (
                <div key={ocRow.id} className={cn('group/oc-row', rowIdx > 0 && 'border-t border-dashed border-gray-200 pt-4')}>
                  <div className="flex items-start justify-between gap-1">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-semibold text-gray-400">#{ocRow.position}</span>
                        {ocArticleNumber && <span className="font-mono text-[10px] text-gray-400">{ocArticleNumber}</span>}
                      </div>
                      {ocRow.article_description && (
                        <p className="mt-0.5 text-sm font-medium leading-snug text-gray-900">{ocRow.article_description}</p>
                      )}
                      {ocRow.additional_information?.trim() && (
                        <p className="mt-0.5 text-xs text-gray-500">{ocRow.additional_information}</p>
                      )}
                      <div className="mt-1.5 space-y-3">
                        {lines.map((line, i) => {
                          const key = ocLineKey(line)
                          return (
                            <div key={key} className={cn(i > 0 && 'border-t border-dashed border-gray-200 pt-3')}>
                              <OcLineCompact
                                line={line} unit={row.unit} row={row} settings={settings}
                                editingField={fieldEdit?.lineKey === key ? fieldEdit.field : null}
                                isSaving={updateMutation.isPending}
                                readOnly={readOnly}
                                highlightDeviations={highlightDeviations}
                                quantityDeviation={quantityDeviation}
                                onRequestEdit={(f) => setFieldEdit({ lineKey: key, field: f })}
                                onCancelEdit={() => setFieldEdit(null)}
                                onSaveField={async (_f, patch) => { await updateMutation.mutateAsync({ line, patch }) }}
                              />
                            </div>
                          )
                        })}
                      </div>
                    </div>
                    {!readOnly && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="flex h-6 w-6 shrink-0 items-center justify-center rounded opacity-0 transition-opacity hover:bg-gray-100 group-hover/oc-row:opacity-100"
                            aria-label="Row actions"
                          >
                            <MoreHorizontal className="h-4 w-4 text-gray-400" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent align="end" side="bottom" className="w-44 p-1">
                          <button
                            type="button"
                            disabled={isUnpairing}
                            onClick={() => unpairMutation.mutate(ocRow.id)}
                            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                          >
                            {isUnpairing
                              ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
                              : <Link2Off className="h-3.5 w-3.5 shrink-0 text-gray-400" />}
                            Unmatch row
                          </button>
                          <button
                            type="button"
                            disabled={duplicateMutation.isPending && duplicateMutation.variables === ocRow.id}
                            onClick={() => duplicateMutation.mutate(ocRow.id)}
                            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                          >
                            {duplicateMutation.isPending && duplicateMutation.variables === ocRow.id
                              ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
                              : <Copy className="h-3.5 w-3.5 shrink-0 text-gray-400" />}
                            Duplicate row
                          </button>
                          {(ocRow.confirmed_quantities?.length ?? 0) > 1 && (
                            <button
                              type="button"
                              disabled={splitQuantitiesMutation.isPending && splitQuantitiesMutation.variables === ocRow.id}
                              onClick={() => splitQuantitiesMutation.mutate(ocRow.id)}
                              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                            >
                              {splitQuantitiesMutation.isPending && splitQuantitiesMutation.variables === ocRow.id
                                ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
                                : <Scissors className="h-3.5 w-3.5 shrink-0 text-gray-400" />}
                              Split quantities
                            </button>
                          )}
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

function UnmatchedArticlesGrid({
  rows,
  currency,
  pendingMatchId,
  readOnly,
  isPairing,
  deletingId,
  onSelectMatch,
  onCancelMatch,
  onDelete,
}: {
  rows: OrderConfirmationRow[]
  currency: string
  pendingMatchId: string | null
  readOnly: boolean
  isPairing: boolean
  deletingId: string | null
  onSelectMatch: (id: string) => void
  onCancelMatch: () => void
  onDelete: (id: string) => void
}) {
  if (rows.length === 0) return null
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="text-xs font-bold uppercase tracking-widest text-gray-500">Unmatched rows</h2>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {rows.map((row) => {
          const isSelected = pendingMatchId === row.id
          const cqs = row.confirmed_quantities ?? []
          const articleNumber = row.supplier_article_number || row.customer_article_number || ''
          const unitPriceLabel = formatUnitPriceLabel(row.unit_price, currency, row.unit)
          return (
            <div
              key={row.id}
              className={cn(
                'flex flex-col gap-2 rounded-lg border p-3 transition-colors',
                isSelected
                  ? 'border-blue-400 bg-blue-50 ring-2 ring-blue-300 ring-offset-1'
                  : 'border-gray-200 bg-white',
              )}
            >
              <div className="min-w-0">
                <div className="flex items-baseline gap-1.5">
                  <span className="text-[10px] font-semibold text-gray-400">#{row.position}</span>
                  {articleNumber && <span className="truncate font-mono text-[10px] text-gray-400">{articleNumber}</span>}
                </div>
                {row.article_description && (
                  <p className="mt-0.5 line-clamp-2 text-sm font-medium leading-snug text-gray-900">{row.article_description}</p>
                )}
                {row.additional_information?.trim() && (
                  <p className="mt-0.5 line-clamp-3 text-xs text-gray-500">{row.additional_information}</p>
                )}
                <div className="mt-1 space-y-1.5">
                  {cqs.map((cq, i) => {
                    const arrivalDate = confirmedQuantityArrivalDate(cq)
                    return (
                    <div key={cq.id ?? i} className={cn('space-y-0.5', i > 0 && 'border-t border-dashed border-gray-100 pt-1.5')}>
                      <p className="text-sm text-gray-700">{cq.quantity} {row.unit}</p>
                      {arrivalDate && <p className="text-xs text-gray-400">{arrivalDate}</p>}
                    </div>
                    )
                  })}
                  {unitPriceLabel && (
                    <p className="text-xs text-gray-400">{unitPriceLabel}</p>
                  )}
                </div>
              </div>
              {!readOnly && (
                <div className="mt-auto flex items-center gap-1.5">
                  {isSelected ? (
                    <button
                      type="button"
                      onClick={onCancelMatch}
                      className="flex flex-1 items-center justify-center gap-1.5 rounded border border-blue-200 bg-blue-100 px-2 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-200"
                    >
                      <X className="h-3 w-3" />
                      Cancel
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={!!pendingMatchId || isPairing}
                      onClick={() => onSelectMatch(row.id)}
                      className="flex-1 rounded border border-gray-200 px-2 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Match row
                    </button>
                  )}
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded border border-gray-200 hover:bg-gray-50"
                        aria-label="Row actions"
                      >
                        <MoreHorizontal className="h-3.5 w-3.5 text-gray-400" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="end" side="top" className="w-40 p-1">
                      <button
                        type="button"
                        disabled={deletingId === row.id}
                        onClick={() => onDelete(row.id)}
                        className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                      >
                        {deletingId === row.id
                          ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
                          : <Trash2 className="h-3.5 w-3.5 shrink-0" />}
                        Delete row
                      </button>
                    </PopoverContent>
                  </Popover>
                </div>
              )}
            </div>
          )
        })}
      </div>
      {pendingMatchId && (
        <p className="text-xs text-blue-600">Now click a purchase order row above to connect this row</p>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Bottom validate bar
// ---------------------------------------------------------------------------

const OC_FIELD_LABELS: Record<string, string> = {
  'order_confirmation_head.customer_order_number': 'Customer order no.',
  'order_confirmation_head.supplier_order_number': 'Supplier order no.',
  'order_confirmation_head.supplier_name': 'Supplier name',
  'order_confirmation_head.supplier_contact_person': 'Supplier contact',
  'order_confirmation_head.payment_terms': 'Payment terms',
  'order_confirmation_head.delivery_terms': 'Delivery terms',
  'order_confirmation_head.delivery_method': 'Delivery method',
  'order_confirmation_head.invoice_address_company_name': 'Invoice company',
  'order_confirmation_head.invoice_address_street': 'Invoice street',
  'order_confirmation_head.invoice_address_city': 'Invoice city',
  'order_confirmation_head.invoice_address_country': 'Invoice country',
  'order_confirmation_head.delivery_address_company_name': 'Delivery company',
  'order_confirmation_head.delivery_address_street': 'Delivery street',
  'order_confirmation_head.delivery_address_city': 'Delivery city',
  'order_confirmation_head.delivery_address_country': 'Delivery country',
  'confirmed_quantity.quantity': 'Quantity',
  'confirmed_quantity.arrival_date': 'Arrival date',
  'confirmed_quantity.delivery_date': 'Delivery date',
  'action:validated': 'Validated',
  'action:canceled': 'Canceled',
  'action:manual_handling': 'Marked for manual handling',
  'action:row_created': 'Row created',
  'action:row_deleted': 'Row deleted',
  'action:row_paired': 'Row paired with PO row',
  'action:row_unpaired': 'Row unpaired from PO row',
  'action:row_duplicated': 'Row duplicated',
  'action:row_split': 'Row split from quantity',
}

function OcAuditLog({ changes }: { changes: OcChange[] }) {
  const [expanded, setExpanded] = useState(false)
  if (changes.length === 0) return null

  // Action events (field_name starts with "action:") are always shown in full — every occurrence
  // is a distinct event. Field edits are deduplicated: only the latest value per (position, field_name) is shown.
  const isActionEvent = (ch: OcChange) => ch.field_name.startsWith('action:')

  const seen = new Set<string>()
  const displayEntries: OcChange[] = []
  for (const ch of changes) {
    if (isActionEvent(ch)) {
      displayEntries.push(ch)
    } else {
      const key = `${ch.position}:${ch.field_name}`
      if (!seen.has(key)) { seen.add(key); displayEntries.push(ch) }
    }
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between py-1 text-left"
      >
        <p className={HEAD_LABEL}>
          Audit log <span className="normal-case font-normal tracking-normal text-gray-300">({displayEntries.length})</span>
        </p>
        {expanded
          ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" />
          : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
      </button>
      {expanded && (
        <ol className="mt-1.5 space-y-1.5">
          {displayEntries.map((ch, i) => {
            const isAction = isActionEvent(ch)
            const rawKey = ch.field_name.split('.').pop() ?? ch.field_name
            const label = OC_FIELD_LABELS[ch.field_name] ?? rawKey.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
            const byName = ch.by ? ch.by.first_name : 'Unknown'
            const date = formatDateTime(ch.timestamp)
            const rowPairDetail = ch.field_name === 'action:row_paired'
              ? ` – row ${ch.position} → PO row ${ch.new_value}`
              : ch.field_name === 'action:row_unpaired'
              ? ` – row ${ch.position}${ch.old_value ? ` (was PO row ${ch.old_value})` : ''}`
              : null
            return (
              <li key={i} className="text-xs text-gray-500">
                <span className="text-gray-400">{date}, {byName}</span>
                <span className="mx-1.5 text-gray-300">-</span>
                <span className="font-medium text-gray-700">{label}</span>
                {rowPairDetail && (
                  <span className="text-gray-500">{rowPairDetail}</span>
                )}
                {!isAction && (
                  <>
                    <span className="mx-1 text-gray-400">→</span>
                    <span className="text-gray-800">{ch.new_value || '-'}</span>
                  </>
                )}
              </li>
            )
          })}
        </ol>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// PO-only row view (shown in waiting / done states)
// ---------------------------------------------------------------------------

function PoRowStatusDot({ row }: { row: PurchaseOrderRow }) {
  if (row.is_canceled) {
    return (
      <Tooltip content="Canceled">
        <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-red-100">
          <X className="h-2.5 w-2.5 text-red-600" strokeWidth={3} />
        </span>
      </Tooltip>
    )
  }
  if (row.is_delivered || row.is_confirmed) {
    return (
      <Tooltip content="Confirmed">
        <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-emerald-100">
          <Check className="h-2.5 w-2.5 text-emerald-700" strokeWidth={3} />
        </span>
      </Tooltip>
    )
  }
  return <span className="h-[18px] w-[18px] shrink-0" />
}

function PoRowsPanel({ rows, currency }: { rows: PurchaseOrderRow[]; currency: string }) {
  if (rows.length === 0) {
    return <div className="px-4 py-10 text-center text-sm text-gray-400">No line items.</div>
  }
  return (
    <div>
      <div className="grid grid-cols-[3fr_1fr_1fr_1fr_18px] items-center gap-x-4 border-b border-gray-100 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
        <div>Article</div>
        <div className="text-right">Qty</div>
        <div className="text-right">Unit price</div>
        <div>Delivery</div>
        <div />
      </div>
      {rows.map((row) => {
        const isInactive = row.is_canceled
        return (
          <div key={row.id} className={cn('grid grid-cols-[3fr_1fr_1fr_1fr_18px] items-start gap-x-4 border-b border-gray-100 px-4 py-2.5', isInactive && 'opacity-50')}>
            <div>
              <div className="flex items-baseline gap-2">
                <span className="text-[10px] font-semibold text-gray-400">#{row.position}</span>
                {row.article_number && <span className="font-mono text-[10px] text-gray-400">{row.article_number}</span>}
              </div>
              <p className={cn('mt-0.5 text-sm font-medium leading-snug', isInactive ? 'text-gray-400' : row.article_description ? 'text-gray-900' : 'italic text-gray-400')}>
                {row.article_description || row.article_number || '—'}
              </p>
              {row.additional_information?.trim() && (
                <p className="mt-0.5 text-xs text-gray-500">{row.additional_information}</p>
              )}
            </div>
            <div className="text-right text-sm tabular-nums text-gray-700">{row.requested_quantity} {row.unit}</div>
            <div className="text-right text-sm tabular-nums text-gray-700">
              {formatUnitPriceLabel(row.unit_price, currency, row.unit) ?? '—'}
            </div>
            <div className="text-xs text-gray-500">
              {row.is_confirmed || row.is_delivered
                ? formatDate(row.confirmed_delivery_date ?? row.requested_delivery_date)
                : null}
            </div>
            <div className="pt-0.5">
              <PoRowStatusDot row={row} />
            </div>
          </div>
        )
      })}
    </div>
  )
}

function BottomBar({ disabled, disabledReason, onValidate, validating, onManualHandling, onSkip }: {
  disabled: boolean
  disabledReason: string | undefined
  onValidate: () => void
  validating: boolean
  onManualHandling: () => void
  onSkip: () => void
}) {
  const validateButton = (
    <span className="inline-flex">
      <Button variant="cta" disabled={disabled || validating} onClick={onValidate}>
        {validating ? (
          <>
            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            Validating…
          </>
        ) : (
          'Validate & Sync ERP'
        )}
      </Button>
    </span>
  )

  return (
    <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-gray-200 bg-white/95 backdrop-blur-sm">
      <div className="container mx-auto flex items-center justify-end gap-2 py-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" className="h-9 w-9 shrink-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="top">
            <DropdownMenuItem onClick={onManualHandling}>
              Move to Manual Handling
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onSkip} className="text-red-600 focus:text-red-600">
              Skip Order Confirmation
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {disabledReason ? <Tooltip content={disabledReason}>{validateButton}</Tooltip> : validateButton}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function PurchaseOrderDetailPage() {
  const { workflowId, purchaseOrderId } = useParams<{ workflowId: string; purchaseOrderId: string }>()
  const [deviationsOnly, setDeviationsOnly] = useState(false)
  const [viewMode, setViewMode] = useState<DetailViewMode>('detail')
  const [sidebarIndex, setSidebarIndex] = useState(Number.MAX_SAFE_INTEGER)
  const [pendingMatchOcRowId, setPendingMatchOcRowId] = useState<string | null>(null)
  const [showValidateConfirm, setShowValidateConfirm] = useState(false)
  const [showSkipConfirm, setShowSkipConfirm] = useState(false)
  const [erpSyncErrors, setErpSyncErrors] = useState<string[]>([])
  const [followUpErrors, setFollowUpErrors] = useState<string[]>([])
  const [showErpSyncErrors, setShowErpSyncErrors] = useState(false)
  const [deletingOcRowId, setDeletingOcRowId] = useState<string | null>(null)

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['purchase-order-validation', workflowId, purchaseOrderId],
    queryFn: () => getPurchaseOrderValidation(purchaseOrderId!),
    enabled: !!workflowId && !!purchaseOrderId,
    ...workflowDetailQueryOptions,
  })
  const isBackgroundRefreshing = isFetching && !isLoading && !!data

  const { data: ocSettings } = useWorkflowOrderConfirmationSettings(workflowId)
  const supplierId = data?.order_confirmation_purchase_order_pairs?.[0]?.purchase_order_head?.supplier_id
  const { data: supplierSettings } = useSupplierSettings(workflowId, supplierId)

  const queryClient = useQueryClient()

  const createOcMutation = useMutation({
    mutationFn: () => createManualOrderConfirmation(purchaseOrderId!),
    onSuccess: () => {
      invalidateOcWorkflowData(queryClient, workflowId, { purchaseOrderId })
      setViewMode('detail')
      toast({ title: 'Order confirmation created' })
    },
    onError: (err: unknown) => toast({ variant: 'destructive', title: 'Could not create OC', description: err instanceof ApiRequestError ? err.message : err instanceof Error ? err.message : 'Something went wrong.' }),
  })

  const validateMutation = useMutation({
    mutationFn: (ocId: string) => validateOrderConfirmation(ocId),
    onSuccess: (data) => {
      invalidateOcWorkflowData(queryClient, workflowId, { purchaseOrderId })
      setViewMode('history')
      setSidebarIndex(Number.MAX_SAFE_INTEGER)
      const erpErrors = (data.erp_errors ?? []).map((e) => e.trim()).filter(Boolean)
      const followUp = (data.follow_up_errors ?? []).map((e) => e.trim()).filter(Boolean)
      if (erpErrors.length > 0 || followUp.length > 0) {
        setErpSyncErrors(erpErrors)
        setFollowUpErrors(followUp)
        setShowErpSyncErrors(true)
        toast({
          title: 'Order confirmation validated',
          description:
            erpErrors.length > 0 && followUp.length > 0
              ? 'Some ERP updates and follow-up steps did not succeed. Review the details.'
              : erpErrors.length > 0
                ? 'Some ERP updates did not succeed. Review the details.'
                : 'A follow-up step did not succeed. Review the details.',
        })
      } else {
        toast({ title: 'Validated & synced to ERP' })
      }
    },
    onError: (err: unknown) => {
      if (err instanceof ApiRequestError && err.erpErrors?.length) {
        setErpSyncErrors(err.erpErrors)
        setShowErpSyncErrors(true)
      }
      toast({
        variant: 'destructive',
        title: 'Could not validate',
        description: err instanceof ApiRequestError ? err.message : err instanceof Error ? err.message : 'Something went wrong.',
      })
    },
  })

  const syncLatestMutation = useMutation({
    mutationFn: () => syncPurchaseOrderLatestVersion(purchaseOrderId!),
    onSuccess: () => {
      invalidateOcWorkflowData(queryClient, workflowId, { purchaseOrderId })
      toast({ title: 'Purchase order synced from ERP' })
    },
    onError: (err: unknown) => toast({
      variant: 'destructive',
      title: 'Could not sync purchase order',
      description: err instanceof ApiRequestError ? err.message : err instanceof Error ? err.message : 'Something went wrong.',
    }),
  })

  const manualHandlingMutation = useMutation({
    mutationFn: (ocId: string) => markManualHandlingOrderConfirmation(ocId),
    onSuccess: () => {
      invalidateOcWorkflowData(queryClient, workflowId, { purchaseOrderId })
      toast({ title: 'Moved to manual handling' })
    },
    onError: (err: unknown) => toast({ variant: 'destructive', title: 'Could not move to manual handling', description: err instanceof ApiRequestError ? err.message : err instanceof Error ? err.message : 'Something went wrong.' }),
  })

  const cancelOcMutation = useMutation({
    mutationFn: (ocId: string) => cancelOrderConfirmation(ocId),
    onSuccess: () => {
      invalidateOcWorkflowData(queryClient, workflowId, { purchaseOrderId })
      toast({ title: 'Order confirmation skipped' })
    },
    onError: (err: unknown) => toast({ variant: 'destructive', title: 'Could not skip order confirmation', description: err instanceof ApiRequestError ? err.message : err instanceof Error ? err.message : 'Something went wrong.' }),
  })

  const pairMutation = useMutation({
    mutationFn: ({ ocRowId, poRowId }: { ocRowId: string; poRowId: string }) =>
      pairOrderConfirmationRow(ocRowId, poRowId),
    onSuccess: () => {
      setPendingMatchOcRowId(null)
      invalidateOcWorkflowData(queryClient, workflowId, { purchaseOrderId })
      toast({ title: 'Row matched' })
    },
    onError: (err: unknown) => toast({ variant: 'destructive', title: 'Could not match', description: err instanceof ApiRequestError ? err.message : err instanceof Error ? err.message : 'Something went wrong.' }),
  })

  const deleteOcRowMutation = useMutation({
    mutationFn: (ocRowId: string) => {
      setDeletingOcRowId(ocRowId)
      return deleteOrderConfirmationRow(ocRowId)
    },
    onSuccess: () => {
      setDeletingOcRowId(null)
      invalidateOcWorkflowData(queryClient, workflowId, { purchaseOrderId })
      toast({ title: 'Row deleted' })
    },
    onError: (err: unknown) => {
      setDeletingOcRowId(null)
      toast({ variant: 'destructive', title: 'Could not delete', description: err instanceof ApiRequestError ? err.message : err instanceof Error ? err.message : 'Something went wrong.' })
    },
  })

  useEffect(() => {
    setDeviationsOnly(false)
    setViewMode('detail')
    setSidebarIndex(Number.MAX_SAFE_INTEGER)
    setPendingMatchOcRowId(null)
    setShowValidateConfirm(false)
    setShowErpSyncErrors(false)
    setErpSyncErrors([])
    setFollowUpErrors([])
  }, [purchaseOrderId])

  if (isLoading) return <div className="flex items-center justify-center py-24 text-sm text-gray-400">Loading…</div>
  if (!data) return <div className="flex items-center justify-center py-24 text-sm text-gray-400">Purchase order not found</div>

  const po = data.purchase_order
  const history = data.order_confirmation_history
  const pairs = data.order_confirmation_purchase_order_pairs
  const latestSnapshot = data.latest_purchase_order_version ?? null
  const effectiveSettings = supplierSettings ?? ocSettings

  const currentEntry =
    history.find((e) => e.internal_status === 'open' && !e.is_validated) ??
    history.find((e) => e.is_current) ??
    [...history].reverse().find((e) => e.is_validated) ??
    history[history.length - 1]
  const viewingPair = pairs.find((p) => p.order_confirmation.id === currentEntry?.id) ?? pairs[pairs.length - 1]
  const erpSnapshot = erpPurchaseOrderSnapshot(latestSnapshot, viewingPair)
  const poWorkflowTabStatus = derivePoWorkflowTabStatus(pairs, history, erpSnapshot.head)
  const canSyncFromErp = canSyncLatestFromErp(pairs, history, poWorkflowTabStatus)

  const queueEntries = history.filter((e) => e.internal_status === 'queued')
  const hasOutOfOrderQueue = currentEntry != null && queueEntries.some(
    (e) => new Date(e.received_at) < new Date(currentEntry.received_at)
  )
  const hasLateArrivingOc = currentEntry != null && history.some(
    (e) => e.is_validated && new Date(e.received_at) > new Date(currentEntry.received_at)
  )
  const isCanceledOrManualHandling = currentEntry?.internal_status === 'canceled' || currentEntry?.internal_status === 'manualHandling'
  const hasCurrentOc = currentEntry?.internal_status === 'open' && !(currentEntry?.is_validated ?? false)

  const isCurrentValidated = currentEntry?.is_validated ?? false
  const tableIsReadOnly = isCurrentValidated || isCanceledOrManualHandling

  const poHead = viewingPair?.purchase_order_head
  const ocHead = viewingPair?.order_confirmation_head
  const currency = poHead?.currency ?? ''
  const poRows = viewingPair?.purchase_order_rows ?? []
  const erpPoHead = erpSnapshot.head
  const erpPoRows = erpSnapshot.rows
  const erpCurrency = erpPoHead?.currency ?? currency
  const unmatchedOcRows = viewingPair?.unmatched_order_confirmation_rows ?? []
  const viewingOc = viewingPair?.order_confirmation
  const isManualOc = ocSourceKind(viewingOc) === 'manual'
  const highlightDeviations = !isCurrentValidated

  // viewingPair falls back to the previous pair during the SNS-trigger gap when a newly-promoted
  // queued OC has no version yet. Guard the BottomBar against that transient state.
  const viewingMatchesCurrent = viewingOc?.id === currentEntry?.id
  const hasActionableOc = Boolean(currentEntry?.internal_status === 'open' && !currentEntry?.is_validated && !isCanceledOrManualHandling && viewingMatchesCurrent)

  // When no current OC exists at all, redirect 'detail' to 'history' so the sidebar is shown.
  // Canceled/manual_handling OCs keep 'detail' mode (read-only content visible, no bottom bar).
  const isOcProcessed = (e: OrderConfirmationHistoryEntry) =>
    e.is_validated || e.internal_status === 'canceled' || e.internal_status === 'manualHandling'
  // History nav count: all processed OCs. When there's an actionable current OC, exclude it
  // since it has its own "Current" button in the nav.
  const historyEntries = history.filter((e) => isOcProcessed(e) && (hasActionableOc ? e.id !== currentEntry?.id : true))
  // Oldest-first so left arrow = older, right arrow = newer. Index defaults to last (newest).
  const sidebarEntries = history.filter((e) => isOcProcessed(e))
  const clampedSidebarIndex = Math.min(sidebarIndex, Math.max(0, sidebarEntries.length - 1))
  const effectiveViewMode: DetailViewMode = !hasCurrentOc && viewMode === 'detail'
    ? (queueEntries.length > 0 ? 'queue' : 'history')
    : viewMode as DetailViewMode

  const stats = computePageStats(
    poRows,
    unmatchedOcRows,
    ocHead,
    poHead,
    effectiveSettings,
    highlightDeviations,
  )
  const blockedReason = manualOcBlockedReason(po, pairs)

  const hasIncompleteCqs = poRows.some((r) =>
    (r.matched_order_confirmation_rows ?? []).some((ocRow) =>
      (ocRow.confirmed_quantities ?? []).some((cq) => !cq.delivery_date?.trim()),
    ),
  )

  const validateDisabled = tableIsReadOnly || hasIncompleteCqs
  const validateDisabledReason = hasIncompleteCqs && !tableIsReadOnly
    ? 'All confirmed quantities must have a delivery date before validating'
    : tableIsReadOnly
      ? 'The current OC has already been validated'
      : undefined

  function handleValidate() {
    if (!viewingOc?.id) return
    if (stats.unmatchedOcRows > 0) {
      setShowValidateConfirm(true)
    } else {
      validateMutation.mutate(viewingOc.id)
    }
  }

  const displayedRows = (deviationsOnly
    ? poRows.filter((r) => {
        const l = flattenConfirmedLines(r.matched_order_confirmation_rows ?? [], currency)
        return l.length === 0 || confirmedLinesHaveDeviation(r, l, effectiveSettings)
      })
    : poRows)

  return (
    <>
      <div className={cn('relative container mx-auto space-y-4', effectiveViewMode === 'detail' && hasActionableOc && 'pb-20')}>
        <ListRefreshIndicator visible={isBackgroundRefreshing} />
        {/* Nav + summary panels + status badge */}
        <div className="flex flex-wrap items-center gap-4">
          <ThreePanelNav
            historyEntries={historyEntries}
            queueEntries={queueEntries}
            hasCurrentOc={hasCurrentOc}
            viewMode={effectiveViewMode}
            onSelect={(mode) => { setViewMode(mode); if (mode !== 'detail') setPendingMatchOcRowId(null) }}
          />
          {MANUAL_OC_CREATION_ENABLED && effectiveViewMode === 'detail' && (
            <>
              <div className="h-10 w-px shrink-0 bg-gray-200" />
              <CreateOcCard blocked={blockedReason} isCreating={createOcMutation.isPending} onConfirm={() => createOcMutation.mutate()} />
            </>
          )}
          {effectiveViewMode === 'detail' && hasActionableOc && (
            <div className="flex min-w-0 flex-1 justify-center">
              <ValidationSummaryPanels stats={stats} />
            </div>
          )}
          {po.external_status !== 'canceled' && (
            <PoWorkflowStatusBar
              status={poWorkflowTabStatus}
              canSyncFromErp={canSyncFromErp}
              onSync={() => syncLatestMutation.mutate()}
              syncing={syncLatestMutation.isPending}
            />
          )}
        </div>

        {effectiveViewMode === 'history' && (
          <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
            <PoSummaryPanel po={po} head={erpPoHead} rows={erpPoRows} currency={erpCurrency} />
            <OcHistorySidebar
              entries={sidebarEntries}
              pairs={pairs}
              purchaseOrderId={po.id}
              index={clampedSidebarIndex}
              onPrev={() => setSidebarIndex(Math.max(0, clampedSidebarIndex - 1))}
              onNext={() => setSidebarIndex(Math.min(sidebarEntries.length - 1, clampedSidebarIndex + 1))}
            />
          </div>
        )}

        {effectiveViewMode === 'queue' && (
          <OcSourceListView
            entries={queueEntries}
            pairs={pairs}
            purchaseOrderId={po.id}
            title="Queue"
            emptyMessage="No OCs on hold."
            columns={3}
          />
        )}

        {effectiveViewMode === 'detail' && (
          <>
            {isCurrentValidated || isCanceledOrManualHandling ? (
              /* Done / Waiting / Canceled / Manual handling — show PO rows only, no OC comparison */
              <PoSummaryPanel po={po} head={erpPoHead} rows={erpPoRows} currency={erpCurrency} />
            ) : (
            /* Active — two-column comparison */
            <>
            {hasOutOfOrderQueue && (
              <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                <div className="space-y-1">
                  <p className="font-medium">Earlier order confirmations are waiting in the queue</p>
                  <p className="text-amber-700">
                    This OC was received after others that have not yet been processed. Validating it now may apply ERP changes out of order.
                    To resolve: mark this OC as <span className="font-medium">Manual handling</span>, let the queued OCs process and validate normally, then move this email back to the inbox so it re-enters as the latest OC.
                  </p>
                </div>
              </div>
            )}
            {hasLateArrivingOc && (
              <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                <div className="space-y-1">
                  <p className="font-medium">This order confirmation arrived after newer ones were already validated</p>
                  <p className="text-amber-700">
                    One or more newer order confirmations have already been validated and synced to your ERP system.
                    This order confirmation is from an earlier point in time, so its details may no longer be accurate.
                    Review the values carefully. If they conflict with what has already been confirmed, consider marking this order confirmation for{' '}
                    <span className="font-medium">Manual handling</span> instead of validating it.
                  </p>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">

              {/* LEFT — PO header + line items in one panel */}
              <Container className="flex min-w-0 flex-col overflow-hidden">
                <POHeaderPanel po={po} head={poHead} />
                {/* The line items are data, so they sit on white like the other tables, down to the panel's
                    bottom edge (the panel stretches to the source column); the head above keeps the panel's tint. */}
                <div className="flex-1 border-t border-gray-200 bg-white">
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
                    <span className="text-xs font-medium text-gray-500">Line items</span>
                    <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5" role="group">
                      <button type="button" onClick={() => setDeviationsOnly(false)}
                        className={cn('rounded-md px-3 py-1.5 text-xs font-medium transition-colors', !deviationsOnly ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900')}>
                        All rows
                      </button>
                      <button type="button" onClick={() => setDeviationsOnly(true)}
                        className={cn('rounded-md px-3 py-1.5 text-xs font-medium transition-colors', deviationsOnly ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900')}>
                        Deviations only
                      </button>
                    </div>
                  </div>
                  {pendingMatchOcRowId && !tableIsReadOnly && (
                    <div className="flex items-center justify-between gap-2 border-b border-blue-100 bg-blue-50 px-4 py-2">
                      <span className="text-xs text-blue-700">
                        <span className="font-medium">Matching mode</span> — click a purchase order row to connect the selected article
                      </span>
                      <button type="button" onClick={() => setPendingMatchOcRowId(null)} className="text-xs font-medium text-blue-500 hover:text-blue-700">
                        Cancel
                      </button>
                    </div>
                  )}
                  <div>
                    <div className="grid grid-cols-2 border-b border-gray-100 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      <div className="px-4 py-2.5">Purchase order</div>
                      <div className="border-l border-gray-200 px-4 py-2.5">Order confirmation</div>
                    </div>
                    {displayedRows.length === 0 ? (
                      <div className="px-4 py-10 text-center text-sm text-gray-400">{deviationsOnly ? 'No deviations.' : 'No line items.'}</div>
                    ) : (
                      displayedRows.map((row) => (
                        <MatchedRow
                          key={row.id}
                          row={row}
                          currency={currency}
                          workflowId={workflowId!}
                          purchaseOrderId={purchaseOrderId!}
                          ocId={viewingOc?.id ?? ''}
                          isManualOc={isManualOc}
                          readOnly={tableIsReadOnly}
                          highlightDeviations={highlightDeviations}
                          settings={effectiveSettings}
                          matchingMode={!!pendingMatchOcRowId && !tableIsReadOnly}
                          onRowClick={pendingMatchOcRowId && !tableIsReadOnly ? () => pairMutation.mutate({ ocRowId: pendingMatchOcRowId, poRowId: row.id }) : undefined}
                        />
                      ))
                    )}
                  </div>
                  {unmatchedOcRows.length > 0 && (
                    <div id="unmatched-rows-section" className="border-t border-gray-200 px-4 py-4">
                      <UnmatchedArticlesGrid
                        rows={unmatchedOcRows}
                        currency={currency}
                        pendingMatchId={pendingMatchOcRowId}
                        readOnly={tableIsReadOnly}
                        isPairing={pairMutation.isPending}
                        deletingId={deletingOcRowId}
                        onSelectMatch={setPendingMatchOcRowId}
                        onCancelMatch={() => setPendingMatchOcRowId(null)}
                        onDelete={(id) => deleteOcRowMutation.mutate(id)}
                      />
                    </div>
                  )}
                </div>
              </Container>

              {/* RIGHT — OC head at top + source below */}
              <Container className="min-w-0 overflow-hidden">
                {ocHead && (
                  <OCHeaderPanel
                    ocHead={ocHead}
                    poHead={poHead}
                    readOnly={tableIsReadOnly}
                    highlightDeviations={highlightDeviations}
                    settings={effectiveSettings}
                    onSave={async (patch) => {
                      if (!viewingOc?.id) return
                      await updateOrderConfirmationHead(viewingOc.id, patch)
                      invalidateOcWorkflowData(queryClient, workflowId, { purchaseOrderId })
                    }}
                  />
                )}
                <div className="border-t border-gray-200 p-4 space-y-4">
                  <OCSourcePanel
                    oc={viewingOc}
                    changes={viewingPair?.order_confirmation_changes ?? []}
                    purchaseOrderId={po.id}
                  />
                </div>
              </Container>
            </div>
            </>
            )}
          </>
        )}

      </div>

      {effectiveViewMode === 'detail' && hasActionableOc && (
        <BottomBar
          disabled={validateDisabled}
          disabledReason={validateDisabledReason}
          validating={validateMutation.isPending}
          onValidate={handleValidate}
          onManualHandling={() => viewingOc && manualHandlingMutation.mutate(viewingOc.id)}
          onSkip={() => setShowSkipConfirm(true)}
        />
      )}

      <Dialog.Root open={showValidateConfirm} onOpenChange={setShowValidateConfirm}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/25 backdrop-blur-[1px]" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-panel bg-white p-6 shadow-lg">
            <Dialog.Title className="text-base font-semibold text-gray-900">Unmatched rows</Dialog.Title>
            <Dialog.Description className="mt-2 text-sm text-gray-500">
              This OC has {stats.unmatchedOcRows} unmatched {stats.unmatchedOcRows === 1 ? 'row' : 'rows'} not connected to any purchase order row. {stats.unmatchedOcRows === 1 ? 'It' : 'They'} will remain unmatched after validation. Do you want to proceed?
            </Dialog.Description>
            <div className="mt-5 flex justify-end gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setShowValidateConfirm(false)}>Cancel</Button>
              <Button
                type="button"
                size="sm"
                disabled={validateMutation.isPending}
                onClick={() => {
                  setShowValidateConfirm(false)
                  if (viewingOc?.id) validateMutation.mutate(viewingOc.id)
                }}
              >
                {validateMutation.isPending ? 'Validating…' : 'Validate anyway'}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <Dialog.Root open={showErpSyncErrors} onOpenChange={setShowErpSyncErrors}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/25 backdrop-blur-[1px]" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[min(85vh,32rem)] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col rounded-panel bg-white p-6 shadow-lg">
            <Dialog.Title className="text-base font-semibold text-gray-900">Validated with warnings</Dialog.Title>
            <Dialog.Description className="mt-2 shrink-0 text-sm text-gray-500">
              The order confirmation was validated, but not everything after it succeeded.
            </Dialog.Description>
            <div className="mt-4 min-h-0 flex-1 space-y-4 overflow-y-auto pr-1 text-sm text-gray-800">
              {erpSyncErrors.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                    Could not be posted to your ERP — review and correct these directly in the ERP
                  </p>
                  <ul className="space-y-2">
                    {erpSyncErrors.map((message, index) => (
                      <li key={`erp-${index}-${message}`} className="rounded-md border border-amber-200/80 bg-amber-50/60 px-3 py-2 leading-snug">
                        {message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {followUpErrors.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                    Follow-up steps that didn&apos;t complete — no action needed from you
                  </p>
                  <ul className="space-y-2">
                    {followUpErrors.map((message, index) => (
                      <li key={`followup-${index}-${message}`} className="rounded-md border border-amber-200/80 bg-amber-50/60 px-3 py-2 leading-snug">
                        {message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="mt-5 flex shrink-0 justify-end">
              <Button type="button" size="sm" onClick={() => setShowErpSyncErrors(false)}>
                OK
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <Dialog.Root open={showSkipConfirm} onOpenChange={setShowSkipConfirm}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/25 backdrop-blur-[1px]" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-panel bg-white p-6 shadow-lg">
            <Dialog.Title className="text-base font-semibold text-gray-900">Skip Order Confirmation</Dialog.Title>
            <Dialog.Description className="mt-2 text-sm text-gray-500">
              Skipping an Order Confirmation marks it as canceled. This is for when an Order Confirmation is invalid. Any associated Purchase Order will be marked as Pending.
            </Dialog.Description>
            <div className="mt-5 flex justify-end gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setShowSkipConfirm(false)}>Cancel</Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                disabled={cancelOcMutation.isPending}
                onClick={() => {
                  setShowSkipConfirm(false)
                  if (viewingOc?.id) cancelOcMutation.mutate(viewingOc.id)
                }}
              >
                {cancelOcMutation.isPending ? 'Skipping…' : 'Skip Order Confirmation'}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  )
}
